import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useMemo } from 'react';
import { FiUpload, FiDownload, FiSearch, FiEdit, FiTrash2, FiX, FiCheck, FiArrowLeft, FiList, FiArrowUp, FiArrowDown, FiRefreshCw } from 'react-icons/fi';
import { getOrders, updateOrder, updateOrderShip } from '../api/orderService';
import * as XLSX from 'xlsx';
import 'react-datepicker/dist/react-datepicker.css';
import api from '../api/axiosConfig';
import { preflightCheonilPrintOrders, savePrintOrders } from '../api/printOrderService';
import { Dropdown, Space } from 'antd';
import { FilterFilled } from '@ant-design/icons';
import 'antd/dist/reset.css';
import './AllocationManagement.css';
const normalizePickingCode = (value) => String(value !== null && value !== void 0 ? value : '').trim().toUpperCase();
const toSortableLocationNumber = (value) => {
    const normalized = String(value !== null && value !== void 0 ? value : '').trim();
    if (!normalized) {
        return Number.MAX_SAFE_INTEGER;
    }
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
};
const sortPickingStockEntries = (entries) => [...entries].sort((left, right) => left.A.localeCompare(right.A, 'ko') ||
    toSortableLocationNumber(left.B) - toSortableLocationNumber(right.B) ||
    toSortableLocationNumber(left.C) - toSortableLocationNumber(right.C) ||
    right.qty - left.qty);
const allocatePickingUnits = (stockMap, component, quantity) => {
    const allocations = [];
    const stockList = stockMap[normalizePickingCode(component.code)] || [];
    let remaining = quantity;
    for (const stock of stockList) {
        if (remaining <= 0) {
            break;
        }
        const availableQty = Math.max(0, Number(stock.qty || 0));
        if (availableQty <= 0) {
            continue;
        }
        const assignedQty = Math.min(availableQty, remaining);
        for (let index = 0; index < assignedQty; index += 1) {
            allocations.push({
                location: `${stock.A}-${stock.B}-${stock.C}-${component.code}`,
                locA: stock.A,
                locB: stock.B,
                locC: stock.C,
                shortage: false,
            });
        }
        stock.qty -= assignedQty;
        remaining -= assignedQty;
    }
    while (remaining > 0) {
        allocations.push({
            location: 'Z-Z-Z',
            locA: 'Z',
            locB: 'Z',
            locC: 'Z',
            shortage: true,
        });
        remaining -= 1;
    }
    return allocations;
};
const COURIER_NAME_ALIASES = {
    '\uACF5\uC6A9': '\uACF5\uC6A9',
    '\uACF5\uB3D9': '\uACF5\uC6A9',
    '\uD654\uBB3C': '\uD654\uBB3C',
    '\uCFE0\uD321': '\uCFE0\uD321',
    'COUPANG': '\uCFE0\uD321',
    '\uCC9C\uC77C': '\uCC9C\uC77C',
    '\uCC9C\uC77C\uD0DD\uBC30': '\uCC9C\uC77C',
    '\uACBD\uB3D9': '\uACBD\uB3D9',
    '\uACBD\uB3D9\uD0DD\uBC30': '\uACBD\uB3D9',
    '\uD55C\uC9C4': '\uD55C\uC9C4',
    '\uD55C\uC9C4\uD0DD\uBC30': '\uD55C\uC9C4\uD0DD\uBC30',
    '\uBBF8\uC9C0\uC815': '\uBBF8\uC9C0\uC815'
};
const normalizeCourierName = (value) => {
    var _a;
    const raw = String(value !== null && value !== void 0 ? value : '').trim();
    if (!raw) {
        return '\uBBF8\uC9C0\uC815';
    }
    if (/^[?\uFFFD]+$/u.test(raw)) {
        return '\uBBF8\uC9C0\uC815';
    }
    const compact = raw.replace(/\s+/g, '');
    const alias = (_a = COURIER_NAME_ALIASES[raw]) !== null && _a !== void 0 ? _a : COURIER_NAME_ALIASES[compact];
    if (alias) {
        return alias;
    }
    if (raw.includes('\uD55C\uC9C4')) {
        return raw.includes('\uD0DD\uBC30') ? '\uD55C\uC9C4\uD0DD\uBC30' : '\uD55C\uC9C4';
    }
    if (raw.includes('\uACBD\uB3D9')) {
        return '\uACBD\uB3D9';
    }
    if (raw.includes('\uCC9C\uC77C')) {
        return '\uCC9C\uC77C';
    }
    if (raw.toUpperCase() === 'COUPANG' || raw.includes('\uCFE0\uD321')) {
        return '\uCFE0\uD321';
    }
    return raw;
};
const ACCESSORY_TAB_PREFIX = 'accessory:';
const ACCESSORY_PRODUCT_TYPE = '부속품';
const isAccessoryProductType = (value) => String(value !== null && value !== void 0 ? value : '').trim() === ACCESSORY_PRODUCT_TYPE;
const isAccessoryTabCourier = (value) => {
    const normalized = normalizeCourierName(value);
    return normalized === '천일' || normalized === '경동' || normalized === '한진' || normalized === '한진택배';
};
const isAccessoryCourierOrder = (order) => {
    var _a, _b, _c;
    return isAccessoryProductType((_a = order === null || order === void 0 ? void 0 : order.productType) !== null && _a !== void 0 ? _a : order === null || order === void 0 ? void 0 : order.product_type) &&
        isAccessoryTabCourier((_c = (_b = order === null || order === void 0 ? void 0 : order.shipComp) !== null && _b !== void 0 ? _b : order === null || order === void 0 ? void 0 : order.ship_comp) !== null && _c !== void 0 ? _c : order === null || order === void 0 ? void 0 : order.courierCompany);
};
const createEmptyTabCounts = () => ({
    all: 0,
    confirm_request: 0,
    common: 0,
    cargo: 0,
    jeju_island: 0,
    high_fee: 0,
    couriers: {},
    accessoryCouriers: {},
});
const MANUAL_BUNDLE_SUPPORTED_COURIERS = new Set([
    '합포장',
    '합배송',
    '택배',
    '한진',
    '한진택배',
    '천일',
    '경동'
]);
const normalizeBundleField = (value) => String(value !== null && value !== void 0 ? value : '').trim();
const normalizeBundlePhone = (value) => normalizeBundleField(value).replace(/[^\d]/g, '');
const normalizeBundleAddress = (value) => normalizeBundleField(value).replace(/\s+/g, ' ');
const buildManualBundleGroupKey = (order) => {
    const recipientName = normalizeBundleField(order.recipientName);
    const mobilePhone = normalizeBundlePhone(order.mobilePhone || order.phone);
    const zipCode = normalizeBundlePhone(order.zipCode);
    const address = normalizeBundleAddress([order.address, order.addressDetail].filter(Boolean).join(' ').trim());
    const channelOrderId = normalizeBundleField(order.channelOrderId);
    const sabangnetOrderNumber = normalizeBundleField(order.sapangNetOrderNumber || order.orderNumber);
    const receiverKey = [recipientName, mobilePhone, zipCode, address].join('|');
    if (recipientName && (address || mobilePhone)) {
        return `R:${receiverKey}`;
    }
    if (channelOrderId && recipientName) {
        return `C:${channelOrderId}|${recipientName}`;
    }
    if (sabangnetOrderNumber && recipientName) {
        return `S:${sabangnetOrderNumber}|${recipientName}`;
    }
    return '';
};
const isBundleTransferCandidate = (order) => {
    if (!order) {
        return false;
    }
    if (normalizeBundleField(order.trackingNumber)) {
        return false;
    }
    const normalizedShipComp = normalizeCourierName(order.shipComp || order.courierCompany || '');
    if (!MANUAL_BUNDLE_SUPPORTED_COURIERS.has(normalizedShipComp)) {
        return false;
    }
    return buildManualBundleGroupKey(order) !== '';
};
const parseOrderDateValue = (value) => {
    const raw = String(value !== null && value !== void 0 ? value : '').trim();
    if (!raw) {
        return null;
    }
    if (/^\d{8}$/.test(raw)) {
        const year = Number(raw.slice(0, 4));
        const month = Number(raw.slice(4, 6)) - 1;
        const day = Number(raw.slice(6, 8));
        return new Date(year, month, day);
    }
    if (/^\d{14}$/.test(raw)) {
        const year = Number(raw.slice(0, 4));
        const month = Number(raw.slice(4, 6)) - 1;
        const day = Number(raw.slice(6, 8));
        const hour = Number(raw.slice(8, 10));
        const minute = Number(raw.slice(10, 12));
        const second = Number(raw.slice(12, 14));
        return new Date(year, month, day, hour, minute, second);
    }
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};
const formatOrderDateValue = (value) => {
    const parsed = parseOrderDateValue(value);
    if (!parsed) {
        return String(value !== null && value !== void 0 ? value : '');
    }
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};
const AllocationManagement = () => {
    // cleaned legacy comment
    const _d = new Date();
    const today = `${_d.getFullYear()}-${String(_d.getMonth() + 1).padStart(2, '0')}-${String(_d.getDate()).padStart(2, '0')}`;
    // cleaned legacy comment
    const [tabCounts, setTabCounts] = useState(createEmptyTabCounts());
    const [tabTotalShippingFee, setTabTotalShippingFee] = useState(0);
    // cleaned legacy comment
    const [orders, setOrders] = useState([]);
    const [selectedOrders, setSelectedOrders] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchColumn, setSearchColumn] = useState('recipientName');
    const [viewMode, setViewMode] = useState('list');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(0);
    const [editingOrder, setEditingOrder] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [completedCount, setCompletedCount] = useState(0);
    const [waitingCount, setWaitingCount] = useState(0);
    const [serverSummary, setServerSummary] = useState({ total: 0, completed: 0, waiting: 0, shippingFee: 0 });
    const [activeTab, setActiveTab] = useState('all');
    const [searchTrigger, setSearchTrigger] = useState(0);
    // cleaned legacy comment
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [orderStatus, setOrderStatus] = useState('');
    const [paymentStatus, setPaymentStatus] = useState('');
    const [shippingStatus, setShippingStatus] = useState('');
    const [channelId, setChannelId] = useState('');
    const [mallId, setMallId] = useState('');
    // cleaned legacy comment
    const [sortConfig, setSortConfig] = useState(null);
    const [showCourierModal, setShowCourierModal] = useState(false);
    const [selectedCourier, setSelectedCourier] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);
    useEffect(() => {
        setShippingStatus('');
        setCurrentPage(1);
        setSearchTrigger((t) => t + 1);
    }, []);
    // cleaned legacy comment
    const [showEditModal, setShowEditModal] = useState(false);
    // cleaned legacy comment
    const [remainCounts, setRemainCounts] = useState({});
    // cleaned legacy comment
    const [filters, setFilters] = useState({});
    const initialFilters = {};
    const [columnFilters, setColumnFilters] = useState(initialFilters);
    // cleaned legacy comment
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const highlightProductCodes = useMemo(() => new Set([
        'YW1004',
        'YWC2080-WH',
        'YWC2080-BK',
        'YW607',
        'YWC2096-GR',
        'YW1016-10',
        'YW1016-11',
        'YWC5003',
        'YWC5005',
        'YWC5004',
        'YWP3053',
        'YWC5008',
        'YWC5007',
        'YWC2115-GR',
        'YWC2115-BR',
        'YWC2087-BK',
        'YWP4052',
        'YWP4052-2',
        'YWP4052-3',
        'YWP4052-4',
        'YWP8032-BB',
        'YWP8032-LP',
        'YWH4001-BK',
        'YWH4001-WH',
        'YWP6023-RD',
        'YWP6023-BK',
        'YWP6023-IV',
        'YWP6038-RD',
        'YWP6039-RD',
        'YWP6040-RD',
        'YWP6041-RD',
        'YWP6038-BK',
        'YWP6039-BK',
        'YWP6040-BK',
        'YWP6041-BK',
        'YWP6038-IV',
        'YWP6039-IV',
        'YWP6040-IV',
        'YWP6041-IV',
        'DWP104',
        'DWE106',
        'DWE139-GR',
        'DWE139-DG',
        'DWE140-GR',
        'DWE140-DG',
        'DWE135',
        'DWR108-GR',
        'DWP210',
        'DWR106-GR',
        'DWR106-MI',
        'DWR106-PK',
        'DWP208-GR',
        'DWP208-MI',
        'DWP208-PK',
        'CWT106-GR',
        'CWT106-BR',
        'CWT106-BG',
        'CWT107-GR',
        'CWT107-BR',
        'CWT107-BG',
        'CWP115',
        'DWR112',
        'DWP103',
        'DDAA21',
        'DDAA21M',
        'DDAA21S',
        'DDAA22L',
        'DDAA22M',
        'DDAA22S',
        'DDAA22SS',
        'WDDAA21S',
        'DWC111',
        'DWC111-BG',
        'DWC111-SK',
        'DWC110',
        'DWC110-BG',
        'DWC110-SK',
        'DWC109',
        'DWC109-BG',
        'DWC109-SK',
        'DWC112',
        'DWC112-BG',
        'DWC112-SK',
        'DWC103-BL',
        'DWC103-GR',
        'DWC103-RD',
        'DWC102',
        'DWC102-BL',
        'DWC102-GR',
        'DWC102-RD',
        'DWC101',
        'DWC101-BL',
        'DWC101-GR',
        'DWC101-RD',
        'DWC104',
        'DWC104-BL',
        'DWC104-GR',
        'DWC104-RD',
        'DDSC31',
        'DDSC31L',
        'DDSC31M',
        'DDSC31S',
        'DDSC31SS',
        'DDSC32L',
        'DDSC32M',
        'DDSC32S',
        'DDSC32SS',
        'DDSC32XL',
        'DDST55',
        'DDST55L',
        'DDST55M',
        'DDST55S',
        'DDST55SS',
        'DDST55XL',
        'DDST56L',
        'DDST56M',
        'DDST56S',
        'DDST56SS',
        'DDST56XL',
        'DDST51',
        'DDST51L',
        'DDST51M',
        'DDST51S',
        'DDST51SS',
        'DDST51XL',
        'DDST52L',
        'DDST52M',
        'DDST52S',
        'DDST52SS',
        'DDST52XL',
        'DWL103-RD',
        'DWL103-BK',
        'DWL102-RD',
        'DWL102-BK',
        'DWL101-RD',
        'DWL101-BK',
        'DWC115',
        'DWC115-BG',
        'DWC115-KA',
        'DWC114',
        'DWC114-BG',
        'DWC114-KA',
        'DWC113',
        'DWC113-BG',
        'DWC113-KA',
        'DWC116',
        'DWC116-BG',
        'DWC116-KA',
        'DDPA19',
        'DDPA19M',
        'DDPA19S',
        'DDPA19SS',
        'DDPA20M',
        'DDPA20S',
        'DDPA20SS',
        'DDAS58',
        'DDAS58L',
        'DDAS58M',
        'DDAS58S',
        'DDAS58SS',
        'DDAS59L',
        'DDAS59M',
        'DDAS59S',
        'DDAS59SS',
        'WDDAS58L',
        'WDDAS59S',
        'DWE110-BL',
        'DWE110-PK',
        'DWE111-BL',
        'DWE111-PK',
        'DWE112-BL',
        'DWE112-PK',
        'DWE113-BL',
        'DWE113-PK',
        'DWE114-BL',
        'DWE114-PK',
        'DWE115-BL',
        'DWE115-PK',
        'DWL106-GR',
        'DWL105-GR',
        'DWL105-RD',
        'DWL104-GR',
        'DWL104-RD'
    ]), []);
    // 1. Define the column list used by the table and export helpers.
    const columns = [
        { key: 'orderDate', label: '\uC8FC\uBB38\uC77C\uC2DC' },
        { key: 'orderType', label: '\uC8FC\uBB38\uC720\uD615' },
        { key: 'shipComp', label: '\uD0DD\uBC30\uC0AC' },
        { key: 'trackingNumber', label: '\uC1A1\uC7A5\uBC88\uD638' },
        { key: 'customerName', label: '\uC8FC\uBB38\uC790\uBA85' },
        { key: 'recipientName', label: '\uC218\uCDE8\uC778\uBA85' },
        { key: 'productCode', label: '\uC0C1\uD488\uCF54\uB4DC' },
        { key: 'productName', label: '\uC0C1\uD488\uBA85' },
        { key: 'quantity', label: '\uC218\uB7C9', isNumeric: true },
        { key: 'boxqty', label: '\uC1A1\uC7A5\uC218\uB7C9', isNumeric: true },
        { key: 'shipCompFee', label: '\uD0DD\uBC30\uC0AC \uBC30\uC1A1\uBE44', isNumeric: true },
        { key: 'shippingFee', label: '\uBC30\uC1A1\uBE44', isNumeric: true },
        { key: 'shippingMethod', label: '\uC6B4\uC1A1' },
        { key: 'optionInfo', label: '\uC635\uC158\uC815\uBCF4' },
        { key: 'orderStatus', label: '\uC8FC\uBB38\uC0C1\uD0DC' },
        { key: 'zipCode', label: '\uC6B0\uD3B8\uBC88\uD638' },
        { key: 'address', label: '\uC8FC\uC18C' },
        { key: 'addressDetail', label: '\uC0C1\uC138\uC8FC\uC18C' },
        { key: 'mobilePhone', label: '\uD734\uB300\uD3F0' },
        { key: 'phone', label: '\uC804\uD654\uBC88\uD638' },
        { key: 'totalAmount', label: '\uCD1D\uC8FC\uBB38\uAE08\uC561', isNumeric: true },
        { key: 'totalShippingFee', label: '\uCD1D\uD0DD\uBC30\uBE44', isNumeric: true },
        { key: 'totalDiscount', label: '\uCD1D\uD560\uC778\uAE08\uC561', isNumeric: true },
        { key: 'paymentStatus', label: '\uACB0\uC81C\uC0C1\uD0DC' },
        { key: 'shippingStatus', label: '\uBC30\uC1A1\uC0C1\uD0DC' },
        { key: 'message', label: '\uBC30\uC1A1\uBA54\uC2DC\uC9C0' },
        { key: 'channelId', label: '\uCC44\uB110ID' },
        { key: 'channelOrderId', label: '\uCC44\uB110\uC8FC\uBB38ID' },
        { key: 'mallId', label: '\uC1FC\uD551\uBAB0ID' },
        { key: 'sapangNetOrderNumber', label: '\uC0AC\uBC29\uB137\uC8FC\uBB38\uBC88\uD638' },
        { key: 'unitPrice', label: '\uB2E8\uAC00', isNumeric: true },
        { key: 'totalPrice', label: '\uCD1D\uAE08\uC561', isNumeric: true },
        { key: 'status', label: '\uC0C1\uD0DC' },
    ];
    // cleaned legacy comment
    const getFilterMenuItems = (columnKey, data) => {
        const uniqueValues = Array.from(new Set(data.map(item => item[columnKey]))).filter(Boolean);
        return uniqueValues.map(value => {
            var _a;
            return ({
                key: value,
                label: (_jsxs(Space, { children: [((_a = filters[columnKey]) === null || _a === void 0 ? void 0 : _a.includes(value)) && _jsx(FiCheck, {}), value] })),
                onClick: () => {
                    const newFilters = { ...filters };
                    if (!newFilters[columnKey]) {
                        newFilters[columnKey] = [value];
                    }
                    else if (newFilters[columnKey].includes(value)) {
                        newFilters[columnKey] = newFilters[columnKey].filter(k => k !== value);
                        if (newFilters[columnKey].length === 0) {
                            delete newFilters[columnKey];
                        }
                    }
                    else {
                        newFilters[columnKey].push(value);
                    }
                    setFilters(newFilters);
                }
            });
        });
    };
    // cleaned legacy comment
    const applyFilters = (data) => {
        return data.filter(item => {
            return Object.entries(filters).every(([key, values]) => {
                return values.includes(String(item[key]));
            });
        });
    };
    const normalizeZipCode = (zipCode) => String(zipCode || '').replace(/[-\s]/g, '');
    const isZipLengthSix = (order) => normalizeZipCode(order.zipCode).length === 6;
    const shouldKeepResolvedCourierForIsland = (order) => Number(order.shippingFee || 0) >= Number(order.shipCompFee || 0) * 3;
    const isExcludedMall = (mallId) => {
        const excludedMalls = ['GS shop', '고도몰', '롯데', '무신사', '알바티엔', '오늘의집', '스마트스토어'];
        return excludedMalls.some((mall) => String(mallId || '').includes(mall));
    };
    const isHighFeeOrder = (order) => Number(order.shipCompFee || 0) > Number(order.shippingFee || 0) &&
        ['착불', '선불', '확인요청'].includes(order.shippingMethod) &&
        !isZipLengthSix(order) &&
        !isExcludedMall(order.mallId || '');
    // cleaned legacy comment
    const isJejuIslandOrder = (order) => !!order.delivery_area_type &&
        order.delivery_area_type !== 'N' &&
        !shouldKeepResolvedCourierForIsland(order) &&
        !isZipLengthSix(order);
    const getExclusiveTabCategory = (order) => {
        if (String(order.shipComp || '') === '확인요청')
            return 'confirm_request';
        if (isJejuIslandOrder(order))
            return 'jeju_island';
        if (isHighFeeOrder(order))
            return 'high_fee';
        const shipComp = String(order.shipComp || '');
        if (shipComp === '怨듭슜' || shipComp.includes('怨듭슜'))
            return 'common';
        if (shipComp === '화물' || shipComp.includes('화물'))
            return 'cargo';
        return shipComp || 'unassigned';
    };
    const fetchOrders = async () => {
        var _a, _b;
        setLoading(true);
        setError(null);
        try {
            // cleaned legacy comment
            const effectiveStart = startDate && endDate && startDate > endDate ? endDate : startDate;
            const effectiveEnd = startDate && endDate && startDate > endDate ? startDate : endDate;
            const response = await getOrders({
                page: currentPage,
                limit: itemsPerPage === 0 ? 100000 : itemsPerPage,
                startDate: effectiveStart,
                endDate: effectiveEnd,
                orderStatus,
                paymentStatus,
                shippingStatus,
                channelId,
                mallId,
                searchTerm,
                searchColumn,
                tabFilter: activeTab
            });
            const mappedOrders = (response.data || []).map((item) => ({
                id: item.id || '',
                orderNumber: item.order_number || '',
                createdAt: formatOrderDateValue(item.created_at || ''),
                // Keep the visible order datetime on the original order timestamp.
                orderDate: formatOrderDateValue(item.order_date || item.created_at || ''),
                orderType: item.order_type || '',
                orderStatus: item.order_status || '',
                trackingNumber: item.tracking_number || '',
                customerName: item.customer_name || '',
                recipientName: item.recipient_name || '',
                zipCode: item.zip_code || '',
                address: item.address || '',
                addressDetail: item.address_detail || '',
                mobilePhone: item.mobile_phone || '',
                phone: item.phone || '',
                totalAmount: Number(item.total_amount) || 0,
                totalShippingFee: Number(item.total_shipping_fee) || 0,
                totalDiscount: Number(item.total_discount) || 0,
                paymentStatus: item.payment_status || '',
                shippingMethod: item.shipping_method || '',
                shippingStatus: item.shipping_status || '',
                message: item.message || '',
                channelId: item.channel_id || '',
                channelOrderId: item.channel_order_id || '',
                mallId: item.mall_id || '',
                sapangNetOrderNumber: item.sapang_net_order_number || '',
                productCode: item.product_code || '',
                productName: item.product_name || '',
                productType: item.product_type || item.productType || '',
                quantity: Number(item.quantity) || 0,
                unitPrice: Number(item.unit_price) || 0,
                boxqty: Number(item.box_qty) || 0,
                totalPrice: Number(item.total_price) || 0,
                shippingFee: Number(item.shipping_fee) || 0,
                optionInfo: item.option_info || '',
                status: item.status || '',
                courierCompany: item.courier_company || '',
                releaseType: item.release_type || '',
                releaseStatus: item.release_status || '',
                workStatus: item.work_status || '',
                ecountTransfer: item.ecount_transfer || false,
                shipComp: normalizeCourierName(item.ship_comp),
                shipCompFee: Number(item.ship_comp_fee) || 0,
                delivery_area_type: item.delivery_area_type || 'N'
            }));
            // cleaned legacy comment
            const sortedOrders = mappedOrders.sort((a, b) => {
                const aZipLength = a.zipCode.replace(/\D/g, '').length;
                const bZipLength = b.zipCode.replace(/\D/g, '').length;
                if (aZipLength === 6 && bZipLength !== 6)
                    return -1;
                if (aZipLength !== 6 && bZipLength === 6)
                    return 1;
                return 0;
            });
            setOrders(sortedOrders);
            setTotalCount(response.total || 0);
            setCompletedCount(response.completedCount || 0);
            setWaitingCount(response.waitingCount || 0);
            setTabCounts({
                ...createEmptyTabCounts(),
                ...(response.tabCounts || {}),
                couriers: ((_a = response.tabCounts) === null || _a === void 0 ? void 0 : _a.couriers) || {},
                accessoryCouriers: ((_b = response.tabCounts) === null || _b === void 0 ? void 0 : _b.accessoryCouriers) || {},
            });
            setTabTotalShippingFee(response.totalShippingFee || 0);
            setServerSummary({
                total: response.total || 0,
                completed: response.completedCount || 0,
                waiting: response.waitingCount || 0,
                shippingFee: response.totalShippingFee || 0
            });
            return true;
        }
        catch (err) {
            setError('주문 목록 조회 중 오류가 발생했습니다.');
            console.error('Error fetching orders:', err);
            setOrders([]);
            setTotalCount(0);
            setCompletedCount(0);
            setWaitingCount(0);
            setTabCounts(createEmptyTabCounts());
            setTabTotalShippingFee(0);
            setServerSummary({ total: 0, completed: 0, waiting: 0, shippingFee: 0 });
            return false;
        }
        finally {
            setLoading(false);
        }
    };
    // cleaned legacy comment
    useEffect(() => {
        fetchOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTrigger, activeTab, currentPage, itemsPerPage]);
    // cleaned legacy comment
    const handleSearch = () => {
        setCurrentPage(1);
        setSearchTrigger(t => t + 1);
    };
    // cleaned legacy comment
    const setQuickDate = (type) => {
        const end = new Date();
        const start = new Date();
        if (type === 'yesterday') {
            start.setDate(start.getDate() - 1);
            end.setDate(end.getDate() - 1);
        }
        else if (type === 'week') {
            start.setDate(start.getDate() - 6);
        }
        else if (type === 'month1') {
            start.setMonth(start.getMonth() - 1);
        }
        else if (type === 'month3') {
            start.setMonth(start.getMonth() - 3);
        }
        setStartDate(start.toISOString().slice(0, 10));
        setEndDate(end.toISOString().slice(0, 10));
        setCurrentPage(1);
        setSearchTrigger(t => t + 1);
    };
    // cleaned legacy comment
    const handleSelectOrder = (orderId) => {
        setSelectedOrders(prev => prev.includes(orderId)
            ? prev.filter(id => id !== orderId)
            : [...prev, orderId]);
    };
    // cleaned legacy comment
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedOrders(cachedSortedOrders.map(order => order.id));
        }
        else {
            setSelectedOrders([]);
        }
    };
    // cleaned legacy comment
    // const handleSearchChange = (e) => setSearchTerm(e.target.value);
    // cleaned legacy comment
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };
    // cleaned legacy comment
    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };
    // cleaned legacy comment
    const handleDeleteOrder = async (orderId) => {
        var _a, _b;
        if (window.confirm('이 주문을 삭제하시겠습니까?')) {
            try {
                setLoading(true);
                // cleaned legacy comment
                const response = await api.get(`/api/orders/delete/${orderId}`);
                if (response.status === 200) {
                    // cleaned legacy comment
                    await fetchOrders();
                    alert('주문이 성공적으로 삭제되었습니다.');
                }
                else {
                    throw new Error('삭제 요청에 실패했습니다.');
                }
            }
            catch (err) {
                console.error('Error deleting order:', err);
                const errorMessage = ((_b = (_a = err.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) || err.message || '주문 삭제 중 오류가 발생했습니다.';
                setError(errorMessage);
                alert(errorMessage);
            }
            finally {
                setLoading(false);
            }
        }
    };
    // cleaned legacy comment
    const handleColumnFilterChange = (key, value) => {
        setColumnFilters(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1);
    };
    // cleaned legacy comment
    // cleaned legacy comment
    const getFilteredOrders = () => {
        let filtered = orders;
        // cleaned legacy comment
        if (startDate && endDate) {
            filtered = filtered.filter(order => {
                const orderDate = parseOrderDateValue(order.createdAt);
                const start = new Date(startDate);
                const end = new Date(endDate);
                end.setHours(23, 59, 59, 999); // 종료일의 끝까지 포함
                return !!orderDate && orderDate >= start && orderDate <= end;
            });
        }
        // cleaned legacy comment
        if (orderStatus) {
            filtered = filtered.filter(order => order.orderStatus === orderStatus);
        }
        // cleaned legacy comment
        if (paymentStatus) {
            filtered = filtered.filter(order => order.paymentStatus === paymentStatus);
        }
        if (shippingStatus) {
            filtered = filtered.filter(order => order.shippingStatus === shippingStatus);
        }
        // cleaned legacy comment
        if (searchTerm) {
            const colMap = {
                recipientName: 'recipientName',
                id: 'id',
                productName: 'productName',
            };
            const col = colMap[searchColumn] || 'recipientName';
            filtered = filtered.filter(order => { var _a; return String((_a = order[col]) !== null && _a !== void 0 ? _a : '').toLowerCase().includes(searchTerm.toLowerCase()); });
        }
        // cleaned legacy comment
        Object.keys(columnFilters).forEach(key => {
            const filterValue = columnFilters[key];
            if (filterValue) {
                filtered = filtered.filter(order => {
                    const value = order[key];
                    if (value === null || value === undefined)
                        return false;
                    return value.toString().toLowerCase().includes(filterValue.toLowerCase());
                });
            }
        });
        // cleaned legacy comment
        filtered = applyFilters(filtered);
        // cleaned legacy comment
        // cleaned legacy comment
        const sortedFiltered = [...filtered].sort((a, b) => {
            const aIsHighlight = highlightProductCodes.has(a.productCode);
            const bIsHighlight = highlightProductCodes.has(b.productCode);
            // cleaned legacy comment
            if (aIsHighlight && !bIsHighlight)
                return -1;
            if (!aIsHighlight && bIsHighlight)
                return 1;
            // cleaned legacy comment
            return 0;
        });
        return sortedFiltered;
    };
    // cleaned legacy comment
    const getCourierCompanies = () => {
        if (Object.keys(tabCounts.couriers).length > 0) {
            return Object.keys(tabCounts.couriers).sort();
        }
        const companies = new Set(orders
            .filter(order => !isAccessoryCourierOrder(order))
            .map(order => order.shipComp));
        return Array.from(companies).sort();
    };
    const getAccessoryCourierCompanies = () => {
        if (Object.keys(tabCounts.accessoryCouriers).length > 0) {
            return Object.keys(tabCounts.accessoryCouriers).sort();
        }
        const companies = new Set(orders
            .filter(order => isAccessoryCourierOrder(order))
            .map(order => order.shipComp));
        return Array.from(companies).sort();
    };
    // cleaned legacy comment
    const getTabCount = (tab) => {
        if (tab === 'all')
            return tabCounts.all;
        if (tab === 'confirm_request')
            return tabCounts.confirm_request;
        if (tab === 'high_fee')
            return tabCounts.high_fee;
        if (tab === 'common')
            return tabCounts.common;
        if (tab === 'cargo')
            return tabCounts.cargo;
        if (tab === 'jeju_island')
            return tabCounts.jeju_island;
        if (tab.startsWith(ACCESSORY_TAB_PREFIX)) {
            const company = tab.slice(ACCESSORY_TAB_PREFIX.length);
            const courier = tabCounts.accessoryCouriers[company];
            if (courier) {
                const excluded = courier.total - courier.visible;
                return `${courier.visible}${excluded > 0 ? ` (제외 ${excluded})` : ''}`;
            }
            return 0;
        }
        // cleaned legacy comment
        const courier = tabCounts.couriers[tab];
        if (courier) {
            const excluded = courier.total - courier.visible;
            return `${courier.visible}${excluded > 0 ? ` (제외 ${excluded})` : ''}`;
        }
        return 0;
    };
    // cleaned legacy comment
    const getTabTotalAmount = (tab) => {
        return cachedFilteredOrders.reduce((total, order) => total + order.totalAmount, 0);
    };
    // cleaned legacy comment
    const getTabTotalShippingFee = (_tab) => {
        return tabTotalShippingFee;
    };
    // cleaned legacy comment
    const handleExcelDownload = () => {
        // cleaned legacy comment
        const wb = XLSX.utils.book_new();
        // cleaned legacy comment
        const allData = orders.map(order => ({
            '주문일시': order.orderDate,
            '주문유형': order.orderType,
            '택배사': order.shipComp || '-',
            '택배사 배송비': order.shipCompFee,
            '배송비': order.shippingFee,
            '상품코드': order.productCode,
            '상품명': order.productName,
            '옵션정보': order.optionInfo || '-',
            '주문상태': order.orderStatus,
            '주문자명': order.customerName,
            '수취인명': order.recipientName,
            '우편번호': order.zipCode,
            '주소': order.address,
            '상세주소': order.addressDetail || '-',
            '휴대폰': order.mobilePhone,
            '전화번호': order.phone || '-',
            '총주문금액': order.totalAmount,
            '총택배비': order.totalShippingFee,
            '총할인금액': order.totalDiscount,
            '결제상태': order.paymentStatus,
            '배송방법': order.shippingMethod,
            '배송상태': order.shippingStatus,
            '배송메시지': order.message || '-',
            '채널ID': order.channelId,
            '채널주문ID': order.channelOrderId,
            '몰ID': order.mallId,
            '사방넷주문번호': order.sapangNetOrderNumber,
            '수량': order.quantity,
            '단가': order.unitPrice,
            '총금액': order.totalPrice,
            '상태': order.status
        }));
        // cleaned legacy comment
        const allSheet = XLSX.utils.json_to_sheet(allData);
        XLSX.utils.book_append_sheet(wb, allSheet, '전체');
        // cleaned legacy comment
        const highFeeData = orders.filter(order => {
            const excludedMalls = [
                'GS shop', '화물샵', '알바티엔', '와이드화물',
                '사운즈', '무신사', '아트박스', '에스샵'
            ];
            // cleaned legacy comment
            const isExcludedMall = excludedMalls.some(mall => { var _a; return (_a = order.mallId) === null || _a === void 0 ? void 0 : _a.toLowerCase().includes(mall.toLowerCase()); });
            if (isExcludedMall)
                return false;
            const isHighFee = Number(order.shipCompFee || 0) > Number(order.shippingFee || 0);
            const isTargetShippingMethod = order.shippingMethod === '착불' ||
                order.shippingMethod === '선불' ||
                order.shippingMethod === '확인요청';
            return isHighFee && isTargetShippingMethod;
        }).map(order => ({
            '주문일시': order.orderDate,
            '주문유형': order.orderType,
            '택배사': order.shipComp || '-',
            '택배사 배송비': order.shipCompFee,
            '배송비': order.shippingFee,
            '상품코드': order.productCode,
            '상품명': order.productName,
            '옵션정보': order.optionInfo || '-',
            '주문상태': order.orderStatus,
            '주문자명': order.customerName,
            '수취인명': order.recipientName,
            '우편번호': order.zipCode,
            '주소': order.address,
            '상세주소': order.addressDetail || '-',
            '휴대폰': order.mobilePhone,
            '전화번호': order.phone || '-',
            '총주문금액': order.totalAmount,
            '총택배비': order.totalShippingFee,
            '총할인금액': order.totalDiscount,
            '결제상태': order.paymentStatus,
            '배송방법': order.shippingMethod,
            '배송상태': order.shippingStatus,
            '배송메시지': order.message || '-',
            '채널ID': order.channelId,
            '채널주문ID': order.channelOrderId,
            '몰ID': order.mallId,
            '사방넷주문번호': order.sapangNetOrderNumber,
            '수량': order.quantity,
            '단가': order.unitPrice,
            '총금액': order.totalPrice,
            '상태': order.status
        }));
        // cleaned legacy comment
        const highFeeSheet = XLSX.utils.json_to_sheet(highFeeData);
        XLSX.utils.book_append_sheet(wb, highFeeSheet, '배송비 초과');
        // cleaned legacy comment
        const commonData = orders
            .filter(order => order.shipComp === '공용')
            .map(order => ({
            '주문일시': order.orderDate,
            '상품명': order.productName,
            '옵션정보': order.optionInfo || '-',
            '수량': order.quantity,
            '주문자명': order.customerName,
            '수취인명': order.recipientName,
            '주문유형': order.orderType,
            '택배사': order.shipComp || '-',
            '택배사 배송비': order.shipCompFee,
            '배송비': order.shippingFee,
            '상품코드': order.productCode,
            '주문상태': order.orderStatus,
            '우편번호': order.zipCode,
            '주소': order.address,
            '상세주소': order.addressDetail || '-',
            '휴대폰': order.mobilePhone,
            '전화번호': order.phone || '-',
            '총주문금액': order.totalAmount,
            '총택배비': order.totalShippingFee,
            '총할인금액': order.totalDiscount,
            '결제상태': order.paymentStatus,
            '배송방법': order.shippingMethod,
            '배송상태': order.shippingStatus,
            '배송메시지': order.message || '-',
            '채널ID': order.channelId,
            '채널주문ID': order.channelOrderId,
            '몰ID': order.mallId,
            '사방넷주문번호': order.sapangNetOrderNumber,
            '단가': order.unitPrice,
            '총금액': order.totalPrice,
            '상태': order.status
        }));
        // cleaned legacy comment
        const commonSheet = XLSX.utils.json_to_sheet(commonData);
        XLSX.utils.book_append_sheet(wb, commonSheet, '공용');
        // cleaned legacy comment
        const cargoData = orders
            .filter(order => order.shipComp === '화물' && !(order.zipCode && order.zipCode.length === 6))
            .map(order => ({
            '수취인명': order.recipientName,
            '상품명': order.productName,
            '수량': order.quantity,
            '주소': order.address,
            '배송비': order.shippingFee
        }));
        // cleaned legacy comment
        const cargoSheet = XLSX.utils.json_to_sheet(cargoData);
        XLSX.utils.book_append_sheet(wb, cargoSheet, '화물');
        // cleaned legacy comment
        const jejuIslandData = orders
            .filter(order => order.delivery_area_type && order.delivery_area_type !== 'N')
            .map(order => ({
            '주문일시': order.orderDate,
            '주문유형': order.orderType,
            '택배사': order.shipComp || '-',
            '택배사 배송비': order.shipCompFee,
            '배송비': order.shippingFee,
            '상품코드': order.productCode,
            '상품명': order.productName,
            '옵션정보': order.optionInfo || '-',
            '주문상태': order.orderStatus,
            '주문자명': order.customerName,
            '수취인명': order.recipientName,
            '우편번호': order.zipCode,
            '주소': order.address,
            '상세주소': order.addressDetail || '-',
            '휴대폰': order.mobilePhone,
            '전화번호': order.phone || '-',
            '총주문금액': order.totalAmount,
            '총택배비': order.totalShippingFee,
            '총할인금액': order.totalDiscount,
            '결제상태': order.paymentStatus,
            '배송방법': order.shippingMethod,
            '배송상태': order.shippingStatus,
            '배송메시지': order.message || '-',
            '채널ID': order.channelId,
            '채널주문ID': order.channelOrderId,
            '몰ID': order.mallId,
            '사방넷주문번호': order.sapangNetOrderNumber,
            '수량': order.quantity,
            '단가': order.unitPrice,
            '총금액': order.totalPrice,
            '상태': order.status
        }));
        // cleaned legacy comment
        const jejuIslandSheet = XLSX.utils.json_to_sheet(jejuIslandData);
        XLSX.utils.book_append_sheet(wb, jejuIslandSheet, '제주_도서산간');
        // cleaned legacy comment
        const courierCompanies = getCourierCompanies().filter(company => company !== '공용' && company !== '화물');
        courierCompanies.forEach(company => {
            const companyData = orders
                .filter(order => order.shipComp === company)
                .map(order => ({
                '주문일시': order.orderDate,
                '주문유형': order.orderType,
                '택배사': order.shipComp || '-',
                '택배사 배송비': order.shipCompFee,
                '배송비': order.shippingFee,
                '상품코드': order.productCode,
                '상품명': order.productName,
                '옵션정보': order.optionInfo || '-',
                '주문상태': order.orderStatus,
                '주문자명': order.customerName,
                '수취인명': order.recipientName,
                '우편번호': order.zipCode,
                '주소': order.address,
                '상세주소': order.addressDetail || '-',
                '휴대폰': order.mobilePhone,
                '전화번호': order.phone || '-',
                '총주문금액': order.totalAmount,
                '총택배비': order.totalShippingFee,
                '총할인금액': order.totalDiscount,
                '결제상태': order.paymentStatus,
                '배송방법': order.shippingMethod,
                '배송상태': order.shippingStatus,
                '배송메시지': order.message || '-',
                '채널ID': order.channelId,
                '채널주문ID': order.channelOrderId,
                '몰ID': order.mallId,
                '사방넷주문번호': order.sapangNetOrderNumber,
                '수량': order.quantity,
                '단가': order.unitPrice,
                '총금액': order.totalPrice,
                '상태': order.status
            }));
            const companySheet = XLSX.utils.json_to_sheet(companyData);
            XLSX.utils.book_append_sheet(wb, companySheet, company || '미지정');
        });
        // cleaned legacy comment
        const fileName = `할당관리_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(wb, fileName);
    };
    // cleaned legacy comment
    const handleLocationPickingExcel = async () => {
        try {
            setLoading(true);
            const allOrdersRes = await getOrders({
                page: 1,
                limit: 5000,
                startDate,
                endDate,
                shippingStatus: '출고대기'
            });
            if (!allOrdersRes.data || allOrdersRes.data.length === 0) {
                alert('조회된 출고대기 주문이 없습니다.');
                return;
            }
            const mappedAllOrders = allOrdersRes.data.map((item) => ({
                id: item.id || '',
                orderNumber: item.order_number || '',
                createdAt: formatOrderDateValue(item.created_at || ''),
                orderDate: formatOrderDateValue(item.order_date || item.created_at || ''),
                orderType: item.order_type || '',
                orderStatus: item.order_status || '',
                trackingNumber: item.tracking_number || '',
                customerName: item.customer_name || '',
                recipientName: item.recipient_name || '',
                zipCode: item.zip_code || '',
                address: item.address || '',
                addressDetail: item.address_detail || '',
                mobilePhone: item.mobile_phone || '',
                phone: item.phone || '',
                totalAmount: Number(item.total_amount) || 0,
                totalShippingFee: Number(item.total_shipping_fee) || 0,
                totalDiscount: Number(item.total_discount) || 0,
                paymentStatus: item.payment_status || '',
                shippingMethod: item.shipping_method || '',
                shippingStatus: item.shipping_status || '',
                message: item.message || '',
                channelId: item.channel_id || '',
                channelOrderId: item.channel_order_id || '',
                mallId: item.mall_id || '',
                sapangNetOrderNumber: item.sapang_net_order_number || '',
                productCode: item.product_code || '',
                productName: item.product_name || '',
                productType: item.product_type || item.productType || '',
                quantity: Number(item.quantity) || 0,
                unitPrice: Number(item.unit_price) || 0,
                boxqty: Number(item.box_qty) || 0,
                totalPrice: Number(item.total_price) || 0,
                shippingFee: Number(item.shipping_fee) || 0,
                optionInfo: item.option_info || '',
                status: item.status || '',
                courierCompany: item.courier_company || '',
                releaseType: item.release_type || '',
                releaseStatus: item.release_status || '',
                workStatus: item.work_status || '',
                ecountTransfer: item.ecount_transfer || false,
                shipComp: item.ship_comp || '',
                shipCompFee: Number(item.ship_comp_fee) || 0,
                delivery_area_type: item.delivery_area_type || 'N'
            }));
            const targetCouriers = ['한진', '경동', '천일'];
            const targetOrders = mappedAllOrders.filter(order => targetCouriers.includes(order.shipComp) &&
                (!order.trackingNumber || order.trackingNumber.toString().trim() === ''));
            if (targetOrders.length === 0) {
                alert('선택한 기간에는 송장을 아직 발행하지 않은 한진, 경동, 천일 출고대기 주문이 없습니다.');
                return;
            }
            const orderIds = targetOrders
                .map((order) => order.id)
                .filter((orderId) => orderId !== undefined && orderId !== null && String(orderId).trim() !== '');
            const excelResponse = await api.post('/api/locations/simulate-picking-excel', {
                orderIds
            }, {
                responseType: 'blob'
            });
            const blob = new Blob([excelResponse.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const contentDisposition = excelResponse.headers['content-disposition'] || '';
            const fileNameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i);
            const encodedFileName = (fileNameMatch === null || fileNameMatch === void 0 ? void 0 : fileNameMatch[1]) || (fileNameMatch === null || fileNameMatch === void 0 ? void 0 : fileNameMatch[2]) || '';
            const fileName = encodedFileName
                ? decodeURIComponent(encodedFileName)
                : `로케이션별_피킹리스트_${new Date().toISOString().split('T')[0]}.xlsx`;
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
            const totalCount = Number(excelResponse.headers['x-simulation-total'] || 0);
            const shortageCount = Number(excelResponse.headers['x-simulation-shortage'] || 0);
            alert(`피킹 리스트 생성 완료!\n- 총 항목: ${totalCount}개\n- 재고 부족(ZZZ): ${shortageCount}개\n\n'재고부족 리스트' 시트에서 별도로 확인 가능합니다.`);
        }
        catch (error) {
            console.error('피킹리스트 생성 오류:', error);
            alert('로케이션별 피킹 리스트 생성 중 오류가 발생했습니다.');
        }
        finally {
            setLoading(false);
        }
    };
    // cleaned legacy comment
    const handleCourierTransfer = async () => {
        if (selectedOrders.length === 0) {
            alert('선택된 주문이 없습니다.');
            return;
        }
        try {
            setLoading(true);
            // cleaned legacy comment
            let kyungdongSuccessCount = 0;
            let kyungdongFailCount = 0;
            // cleaned legacy comment
            const validOrderIds = selectedOrders.filter(orderId => {
                const order = orders.find(o => o.id === orderId);
                if (!order)
                    return false;
                // cleaned legacy comment
                if (order.trackingNumber && order.trackingNumber.trim() !== '') {
                    return false;
                }
                return true;
            });
            // cleaned legacy comment
            const excludedCount = selectedOrders.length - validOrderIds.length;
            if (excludedCount > 0) {
                const proceed = window.confirm(`선택한 주문 중 ${excludedCount}건은 이미 운송장번호가 존재하여 제외됩니다.\n나머지 ${validOrderIds.length}건을 처리하시겠습니까?`);
                if (!proceed) {
                    setLoading(false);
                    return;
                }
            }
            if (validOrderIds.length === 0) {
                alert('처리할 주문이 없습니다. (모든 주문에 이미 운송장번호가 존재합니다.)');
                setLoading(false);
                return;
            }
            // cleaned legacy comment
            const ordersByCourier = validOrderIds.reduce((acc, orderId) => {
                const order = orders.find(o => o.id === orderId);
                if (order && order.shipComp) {
                    if (!acc[order.shipComp]) {
                        acc[order.shipComp] = [];
                    }
                    acc[order.shipComp].push(orderId);
                }
                return acc;
            }, {});
            // cleaned legacy comment
            // cleaned legacy comment
            const results = await Promise.allSettled(Object.entries(ordersByCourier).map(async ([courier, orderIds]) => {
                let apiUrl = '';
                // cleaned legacy comment
                switch (courier) {
                    case '한진':
                        apiUrl = `/api/courier/hanjin`;
                        break;
                    case '천일':
                        apiUrl = `/api/courier/cheonil2`;
                        break;
                    case '경동':
                        apiUrl = `/api/courier/kyungdong2`;
                        break;
                    default:
                        throw new Error(`지원하지 않는 택배사입니다: ${courier}`);
                }
                // cleaned legacy comment
                const response = await api.post(apiUrl, {
                    orderIds,
                    allowBundleOrders: true,
                });
                return {
                    courier,
                    success: true,
                    data: response.data,
                    successCount: response.data.successCount || orderIds.length,
                    failCount: response.data.failCount || 0
                };
            }));
            // cleaned legacy comment
            let totalSuccessCount = 0;
            let totalFailCount = 0;
            const courierResults = [];
            // cleaned legacy comment
            const allFailureDetails = [];
            results.forEach((result) => {
                var _a;
                if (result.status === 'fulfilled') {
                    const { courier, successCount, failCount, data } = result.value;
                    totalSuccessCount += successCount;
                    totalFailCount += failCount;
                    courierResults.push(`${courier}: 성공 ${successCount}건 / 실패 ${failCount}건`);
                    if (courier === '천일' && (data === null || data === void 0 ? void 0 : data.cheonilTransferSummary)) {
                        const summary = data.cheonilTransferSummary;
                        courierResults.push(`천일 외부접수: ${summary.externalRowCount}행 / 연계수량합계 ${summary.externalLabelCount}건 / 묶음감소 ${summary.collapsedRowDelta}건`);
                    }
                    if (courier === '천일' && ((_a = data === null || data === void 0 ? void 0 : data.cheonilAnalysis) === null || _a === void 0 ? void 0 : _a.summary)) {
                        const summary = data.cheonilAnalysis.summary;
                        courierResults.push(`천일 분석: 발행송장 ${summary.issuedLabelCount}건 / tb_orders_seq ${summary.seqLabelCount}건 / 차이 ${summary.difference}건`);
                        if (summary.mismatchOrderCount > 0) {
                            courierResults.push(`천일 불일치 주문: ${summary.mismatchOrderCount}건 (누락 ${summary.missingSeqLabelCount} / 초과 ${summary.extraSeqLabelCount})`);
                        }
                    }
                    // cleaned legacy comment
                    if (failCount > 0 && (data === null || data === void 0 ? void 0 : data.results)) {
                        const failedItems = data.results.filter((r) => r.result !== 'Y');
                        const byCode = {};
                        failedItems.forEach((r) => {
                            var _a, _b;
                            const code = r.code || '\uD734\uB300\uD3F0놁쓬';
                            const msg = ((_a = r.message) === null || _a === void 0 ? void 0 : _a.slice(0, 60)) || '';
                            if (!byCode[code])
                                byCode[code] = { count: 0, msg, samples: [] };
                            byCode[code].count++;
                            if (byCode[code].samples.length < 3)
                                byCode[code].samples.push(((_b = r.content) === null || _b === void 0 ? void 0 : _b.prodRegNo) || '');
                        });
                        Object.entries(byCode).forEach(([code, info]) => {
                            allFailureDetails.push(`  [${courier}] ${code} (${info.count}건): ${info.msg}`);
                            allFailureDetails.push(`    예시: ${info.samples.join(', ')}`);
                        });
                    }
                }
                else {
                    console.error('택배사 전송 실패:', result.reason);
                    courierResults.push(`택배사 전송 실패: ${result.reason.message}`);
                }
            });
            // cleaned legacy comment
            const resultMessage = [
                `택배사 전송이 완료되었습니다.`,
                `총 처리: ${totalSuccessCount + totalFailCount}건`,
                `성공: ${totalSuccessCount}건 / 실패: ${totalFailCount}건`,
                '',
                '택배사별 상세:',
                ...courierResults,
                ...(allFailureDetails.length > 0 ? ['', '실패 원인:'] : []),
                ...allFailureDetails
            ].join('\n');
            alert(resultMessage);
            // cleaned legacy comment
            fetchOrders();
            setSelectedOrders([]);
        }
        catch (error) {
            console.error('택배사 전송 중 오류 발생:', error);
            alert('택배사 전송 중 오류가 발생했습니다.');
        }
        finally {
            setLoading(false);
        }
    };
    // cleaned legacy comment
    const handleCourierTransfer2 = async () => {
        if (selectedOrders.length === 0) {
            alert('선택된 주문이 없습니다.');
            return;
        }
        try {
            setLoading(true);
            // cleaned legacy comment
            let kyungdongSuccessCount = 0;
            let kyungdongFailCount = 0;
            // cleaned legacy comment
            const validOrderIds = selectedOrders.filter(orderId => {
                const order = orders.find(o => o.id === orderId);
                if (!order)
                    return false;
                // cleaned legacy comment
                if (order.trackingNumber && order.trackingNumber.trim() !== '') {
                    return false;
                }
                return true;
            });
            // cleaned legacy comment
            const excludedCount = selectedOrders.length - validOrderIds.length;
            if (excludedCount > 0) {
                const proceed = window.confirm(`선택한 주문 중 ${excludedCount}건은 이미 운송장번호가 존재하여 제외됩니다.\n나머지 ${validOrderIds.length}건을 처리하시겠습니까?`);
                if (!proceed) {
                    setLoading(false);
                    return;
                }
            }
            if (validOrderIds.length === 0) {
                alert('처리할 주문이 없습니다. (모든 주문에 이미 운송장번호가 존재합니다.)');
                setLoading(false);
                return;
            }
            // cleaned legacy comment
            const ordersByCourier = validOrderIds.reduce((acc, orderId) => {
                const order = orders.find(o => o.id === orderId);
                if (order && order.shipComp) {
                    if (!acc[order.shipComp]) {
                        acc[order.shipComp] = [];
                    }
                    acc[order.shipComp].push(orderId);
                }
                return acc;
            }, {});
            // cleaned legacy comment
            // cleaned legacy comment
            const results = await Promise.allSettled(Object.entries(ordersByCourier).map(async ([courier, orderIds]) => {
                let apiUrl = '';
                // cleaned legacy comment
                switch (courier) {
                    case '한진':
                        apiUrl = `/api/courier/hanjin`;
                        break;
                    case '천일':
                        apiUrl = `/api/courier/cheonil2`;
                        break;
                    case '경동':
                        apiUrl = `/api/courier/kyungdong`;
                        break;
                    default:
                        throw new Error(`지원하지 않는 택배사입니다: ${courier}`);
                }
                // cleaned legacy comment
                const response = await api.post(apiUrl, {
                    orderIds,
                    allowBundleOrders: true,
                });
                return {
                    courier,
                    success: true,
                    data: response.data,
                    successCount: response.data.successCount || orderIds.length,
                    failCount: response.data.failCount || 0
                };
            }));
            // cleaned legacy comment
            let totalSuccessCount = 0;
            let totalFailCount = 0;
            const courierResults = [];
            // cleaned legacy comment
            const allFailureDetails = [];
            results.forEach((result) => {
                var _a, _b, _c, _e, _f;
                if (result.status === 'fulfilled') {
                    const { courier, successCount, failCount, data } = result.value;
                    totalSuccessCount += successCount;
                    totalFailCount += failCount;
                    courierResults.push(`${courier}: 성공 ${successCount}건 / 실패 ${failCount}건`);
                    if (courier === '천일' && (data === null || data === void 0 ? void 0 : data.cheonilTransferSummary)) {
                        const summary = data.cheonilTransferSummary;
                        courierResults.push(`천일 외부접수: ${summary.externalRowCount}행 / 연계수량합계 ${summary.externalLabelCount}건 / 묶음감소 ${summary.collapsedRowDelta}건`);
                    }
                    if (courier === '천일' && ((_a = data === null || data === void 0 ? void 0 : data.cheonilAnalysis) === null || _a === void 0 ? void 0 : _a.summary)) {
                        const summary = data.cheonilAnalysis.summary;
                        courierResults.push(`천일 분석: 발행송장 ${summary.issuedLabelCount}건 / tb_orders_seq ${summary.seqLabelCount}건 / 차이 ${summary.difference}건`);
                        if (summary.mismatchOrderCount > 0) {
                            courierResults.push(`천일 불일치 주문: ${summary.mismatchOrderCount}건 (누락 ${summary.missingSeqLabelCount} / 초과 ${summary.extraSeqLabelCount})`);
                        }
                    }
                    // cleaned legacy comment
                    if (failCount > 0 && (data === null || data === void 0 ? void 0 : data.results)) {
                        const failedItems = data.results.filter((r) => r.result !== 'Y');
                        const byCode = {};
                        failedItems.forEach((r) => {
                            var _a, _b;
                            const code = r.code || '\uD734\uB300\uD3F0놁쓬';
                            const msg = ((_a = r.message) === null || _a === void 0 ? void 0 : _a.slice(0, 60)) || '';
                            if (!byCode[code])
                                byCode[code] = { count: 0, msg, samples: [] };
                            byCode[code].count++;
                            if (byCode[code].samples.length < 3)
                                byCode[code].samples.push(((_b = r.content) === null || _b === void 0 ? void 0 : _b.prodRegNo) || '');
                        });
                        Object.entries(byCode).forEach(([code, info]) => {
                            allFailureDetails.push(`  [${courier}] ${code} (${info.count}건): ${info.msg}`);
                            allFailureDetails.push(`    예시: ${info.samples.join(', ')}`);
                        });
                    }
                }
                else {
                    const errorMessage = ((_e = (_c = (_b = result.reason) === null || _b === void 0 ? void 0 : _b.response) === null || _c === void 0 ? void 0 : _c.data) === null || _e === void 0 ? void 0 : _e.message) ||
                        ((_f = result.reason) === null || _f === void 0 ? void 0 : _f.message) ||
                        '알 수 없는 오류';
                    console.error('택배사 전송 실패:', result.reason);
                    courierResults.push(`택배사 전송 실패: ${errorMessage}`);
                }
            });
            // cleaned legacy comment
            const resultMessage = [
                `택배사 전송이 완료되었습니다.`,
                `총 처리: ${totalSuccessCount + totalFailCount}건`,
                `성공: ${totalSuccessCount}건 / 실패: ${totalFailCount}건`,
                '',
                '택배사별 상세:',
                ...courierResults,
                ...(allFailureDetails.length > 0 ? ['', '실패 원인:'] : []),
                ...allFailureDetails
            ].join('\n');
            alert(resultMessage);
            // cleaned legacy comment
            fetchOrders();
            setSelectedOrders([]);
        }
        catch (error) {
            console.error('택배사 전송 중 오류 발생:', error);
            alert('택배사 전송 중 오류가 발생했습니다.');
        }
        finally {
            setLoading(false);
        }
    };
    const handleBundleTransferSeparated = async () => {
        var _a, _b, _c, _e, _f, _g, _h, _j, _k, _l;
        if (selectedOrders.length === 0) {
            alert('선택된 주문이 없습니다.');
            return;
        }
        try {
            setLoading(true);
            const selectedOrderData = orders.filter(order => selectedOrders.includes(order.id));
            const validBundleOrders = selectedOrderData.filter(isBundleTransferCandidate);
            const excludedCount = selectedOrderData.length - validBundleOrders.length;
            if (excludedCount > 0) {
                const proceed = window.confirm(`선택한 주문 중 ${excludedCount}건은 합배송 전송 대상이 아니어서 제외됩니다.\n나머지 ${validBundleOrders.length}건만 처리할까요?`);
                if (!proceed) {
                    setLoading(false);
                    return;
                }
            }
            if (validBundleOrders.length === 0) {
                alert('합배송 전송 대상 주문이 없습니다.');
                setLoading(false);
                return;
            }
            const onlyHanjinBundleOrders = false;
            if (onlyHanjinBundleOrders) {
                const hanjinResponse = await api.post('/api/courier/hanjin', {
                    orderIds: validBundleOrders.map(order => order.id),
                    allowBundleOrders: true
                });
                const hanjinSuccessCount = Number(((_a = hanjinResponse.data) === null || _a === void 0 ? void 0 : _a.successCount) || 0);
                const hanjinFailCount = Number(((_b = hanjinResponse.data) === null || _b === void 0 ? void 0 : _b.failCount) || 0);
                const hanjinResults = Array.isArray((_c = hanjinResponse.data) === null || _c === void 0 ? void 0 : _c.results)
                    ? hanjinResponse.data.results
                    : [];
                if (hanjinFailCount > 0) {
                    const failureMessage = hanjinResults
                        .filter((item) => (item === null || item === void 0 ? void 0 : item.success) !== true)
                        .map((item) => `${item.orderNumber || item.orderNo || '-'}: ${item.message || 'Hanjin transfer failed'}`)
                        .join('\n');
                    alert(`Hanjin bundle transfer result\nSuccess: ${hanjinSuccessCount}\nFail: ${hanjinFailCount}\n\n${failureMessage}`);
                }
                else {
                    alert(`Hanjin bundle transfer completed\nSuccess: ${hanjinSuccessCount}`);
                }
                fetchOrders();
                setSelectedOrders([]);
                return;
            }
            const response = await api.post('/api/courier/bundle-transfer', {
                orderIds: validBundleOrders.map(order => order.id)
            });
            const successCount = Number(((_e = response.data) === null || _e === void 0 ? void 0 : _e.successCount) || 0);
            const failCount = Number(((_f = response.data) === null || _f === void 0 ? void 0 : _f.failCount) || 0);
            const failedGroups = Array.isArray((_h = (_g = response.data) === null || _g === void 0 ? void 0 : _g.results) === null || _h === void 0 ? void 0 : _h.failedGroups)
                ? response.data.results.failedGroups
                : [];
            if (failCount > 0) {
                const failureMessage = failedGroups
                    .map((group) => { var _a; return `${group.bundleLabel || ((_a = group.orderNumbers) === null || _a === void 0 ? void 0 : _a[0]) || '-'}: ${group.message}`; })
                    .join('\n');
                alert(`합배송 전송 결과\n성공: ${successCount}건\n실패: ${failCount}건\n\n${failureMessage}`);
            }
            else {
                alert(((_j = response.data) === null || _j === void 0 ? void 0 : _j.message) || '합배송 전송이 완료되었습니다.');
            }
            fetchOrders();
            setSelectedOrders([]);
        }
        catch (error) {
            const message = ((_l = (_k = error === null || error === void 0 ? void 0 : error.response) === null || _k === void 0 ? void 0 : _k.data) === null || _l === void 0 ? void 0 : _l.message) ||
                '합배송 전송 중 오류가 발생했습니다.';
            console.error('합배송 전송 중 오류 발생:', error);
            alert(message);
        }
        finally {
            setLoading(false);
        }
    };
    const handleRegularCourierTransferSeparated = async () => {
        if (selectedOrders.length === 0) {
            alert('선택된 주문이 없습니다.');
            return;
        }
        try {
            setLoading(true);
            const validOrderIds = selectedOrders.filter(orderId => {
                const order = orders.find(o => o.id === orderId);
                if (!order)
                    return false;
                if (order.trackingNumber && order.trackingNumber.trim() !== '')
                    return false;
                return true;
            });
            const excludedCount = selectedOrders.length - validOrderIds.length;
            if (excludedCount > 0) {
                const proceed = window.confirm(`선택한 주문 중 ${excludedCount}건은 이미 운송장번호가 있거나 일반 택배사 전송 대상이 아니어서 제외됩니다.\n나머지 ${validOrderIds.length}건만 처리할까요?`);
                if (!proceed) {
                    setLoading(false);
                    return;
                }
            }
            if (validOrderIds.length === 0) {
                alert('택배사 전송 대상 주문이 없습니다.');
                setLoading(false);
                return;
            }
            const ordersByCourier = validOrderIds.reduce((acc, orderId) => {
                const order = orders.find(o => o.id === orderId);
                if (order && order.shipComp) {
                    const normalizedShipComp = normalizeCourierName(order.shipComp);
                    if (!acc[normalizedShipComp]) {
                        acc[normalizedShipComp] = [];
                    }
                    acc[normalizedShipComp].push(orderId);
                }
                return acc;
            }, {});
            const results = await Promise.allSettled(Object.entries(ordersByCourier).map(async ([courier, orderIds]) => {
                let apiUrl = '';
                switch (courier) {
                    case '한진':
                        apiUrl = `/api/courier/hanjin`;
                        break;
                    case '천일':
                        apiUrl = `/api/courier/cheonil2`;
                        break;
                    case '경동':
                        apiUrl = `/api/courier/kyungdong`;
                        break;
                    default:
                        throw new Error(`지원하지 않는 택배사입니다: ${courier}`);
                }
                const response = await api.post(apiUrl, { orderIds });
                return {
                    courier,
                    success: true,
                    data: response.data,
                    successCount: response.data.successCount || orderIds.length,
                    failCount: response.data.failCount || 0
                };
            }));
            let totalSuccessCount = 0;
            let totalFailCount = 0;
            const courierResults = [];
            const allFailureDetails = [];
            results.forEach((result) => {
                var _a, _b, _c, _e, _f;
                if (result.status === 'fulfilled') {
                    const { courier, successCount, failCount, data } = result.value;
                    totalSuccessCount += successCount;
                    totalFailCount += failCount;
                    courierResults.push(`${courier}: 성공 ${successCount}건 / 실패 ${failCount}건`);
                    if (courier === '천일' && (data === null || data === void 0 ? void 0 : data.cheonilTransferSummary)) {
                        const summary = data.cheonilTransferSummary;
                        courierResults.push(`천일 외부접수: ${summary.externalRowCount}행 / 연계수량합계 ${summary.externalLabelCount}건 / 묶음감소 ${summary.collapsedRowDelta}건`);
                    }
                    if (courier === '천일' && ((_a = data === null || data === void 0 ? void 0 : data.cheonilAnalysis) === null || _a === void 0 ? void 0 : _a.summary)) {
                        const summary = data.cheonilAnalysis.summary;
                        courierResults.push(`천일 분석: 발행송장 ${summary.issuedLabelCount}건 / tb_orders_seq ${summary.seqLabelCount}건 / 차이 ${summary.difference}건`);
                        if (summary.mismatchOrderCount > 0) {
                            courierResults.push(`천일 불일치 주문: ${summary.mismatchOrderCount}건 (누락 ${summary.missingSeqLabelCount} / 초과 ${summary.extraSeqLabelCount})`);
                        }
                    }
                    if (failCount > 0 && (data === null || data === void 0 ? void 0 : data.results)) {
                        const failedItems = data.results.filter((r) => r.result !== 'Y' && r.success !== true);
                        failedItems.forEach((r) => {
                            var _a;
                            allFailureDetails.push(`[${courier}] ${r.orderNumber || ((_a = r.content) === null || _a === void 0 ? void 0 : _a.prodRegNo) || '-'}: ${r.message || r.resultMessage || '전송 실패'}`);
                        });
                    }
                }
                else {
                    const errorMessage = ((_e = (_c = (_b = result.reason) === null || _b === void 0 ? void 0 : _b.response) === null || _c === void 0 ? void 0 : _c.data) === null || _e === void 0 ? void 0 : _e.message) ||
                        ((_f = result.reason) === null || _f === void 0 ? void 0 : _f.message) ||
                        '알 수 없는 오류';
                    console.error('택배사 전송 실패:', result.reason);
                    courierResults.push(`택배사 전송 실패: ${errorMessage}`);
                }
            });
            const resultMessage = [
                `택배사 전송이 완료되었습니다.`,
                `총 처리: ${totalSuccessCount + totalFailCount}건`,
                `성공: ${totalSuccessCount}건 / 실패: ${totalFailCount}건`,
                '',
                '택배사별 상세:',
                ...courierResults,
                ...(allFailureDetails.length > 0 ? ['', '실패 상세:'] : []),
                ...allFailureDetails
            ].join('\n');
            alert(resultMessage);
            fetchOrders();
            setSelectedOrders([]);
        }
        catch (error) {
            console.error('택배사 전송 중 오류 발생:', error);
            alert('택배사 전송 중 오류가 발생했습니다.');
        }
        finally {
            setLoading(false);
        }
    };
    const handleSabangnetTransfer = async () => {
        var _a, _b, _c, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
        if (selectedOrders.length === 0) {
            alert('전송할 주문을 선택해주세요.');
            return;
        }
        try {
            setLoading(true);
            // cleaned legacy comment
            const selectedOrderData = orders.filter(order => selectedOrders.includes(order.id));
            const orderIds = Array.from(new Set(selectedOrderData
                .map(order => Number(order.id))
                .filter((value) => Number.isFinite(value))));
            const orderNumbers = Array.from(new Set(selectedOrderData
                .map(order => order.sapangNetOrderNumber || order.orderNumber)
                .map(value => String(value || '').trim())
                .filter(Boolean)));
            if (orderIds.length === 0) {
                alert('선택한 주문의 내부 ID를 찾을 수 없습니다.');
                return;
            }
            const invalidOrders = selectedOrderData.filter(order => !String(order.trackingNumber || '').trim());
            if (invalidOrders.length > 0) {
                alert('송장번호가 없는 주문이 있습니다: ' + invalidOrders.map(order => order.id).join(', '));
                return;
            }
            // cleaned legacy comment
            const response = await api.post(`/api/sabangnet/tracking`, {
                orderIds,
                orderNumbers
            });
            if (response.status === 200 && ((_a = response.data) === null || _a === void 0 ? void 0 : _a.success) !== false) {
                const requestedCount = Number((_c = (_b = response.data) === null || _b === void 0 ? void 0 : _b.requestedCount) !== null && _c !== void 0 ? _c : orderNumbers.length);
                const actualSendCount = Number((_h = (_f = (_e = response.data) === null || _e === void 0 ? void 0 : _e.actualSendCount) !== null && _f !== void 0 ? _f : (_g = response.data) === null || _g === void 0 ? void 0 : _g.count) !== null && _h !== void 0 ? _h : 0);
                const excludedCount = Number((_k = (_j = response.data) === null || _j === void 0 ? void 0 : _j.excludedCount) !== null && _k !== void 0 ? _k : Math.max(0, requestedCount - actualSendCount));
                alert(((_l = response.data) === null || _l === void 0 ? void 0 : _l.message) ||
                    `${requestedCount}건 중 ${actualSendCount}건을 사방넷에 실제 전송했습니다.`
                        + (excludedCount > 0 ? ` (이미 전송 또는 제외 ${excludedCount}건)` : ''));
            }
            else {
                throw new Error(((_m = response.data) === null || _m === void 0 ? void 0 : _m.message) || ((_o = response.data) === null || _o === void 0 ? void 0 : _o.error) || '사방넷 전송 실패');
            }
        }
        catch (error) {
            const axiosError = error;
            const responseData = (_p = axiosError === null || axiosError === void 0 ? void 0 : axiosError.response) === null || _p === void 0 ? void 0 : _p.data;
            console.error('사방넷 전송 오류:', {
                message: axiosError === null || axiosError === void 0 ? void 0 : axiosError.message,
                status: (_q = axiosError === null || axiosError === void 0 ? void 0 : axiosError.response) === null || _q === void 0 ? void 0 : _q.status,
                data: responseData
            });
            alert((responseData === null || responseData === void 0 ? void 0 : responseData.message) ||
                (responseData === null || responseData === void 0 ? void 0 : responseData.error) ||
                (axiosError === null || axiosError === void 0 ? void 0 : axiosError.message) ||
                '사방넷 전송 중 오류가 발생했습니다.');
        }
        finally {
            setLoading(false);
        }
    };
    const handleSabangnetCheck = () => {
        const baseUrl = process.env.REACT_APP_SABANGNET_PUBLIC_BASE_URL || 'http://ydwms.co.kr';
        const xmlUrl = `${baseUrl}/sabangnet_tracking_order.xml`;
        window.open(`https://sbadmin14.sabangnet.co.kr/RTL_API/xml_order_invoice.html?xml_url=${encodeURIComponent(xmlUrl)}`, '_blank');
    };
    const handleCourierMove = () => {
        if (selectedOrders.length === 0) {
            alert('선택된 주문이 없습니다.');
            return;
        }
        setShowCourierModal(true);
    };
    const handleUndeliverable = () => {
        // cleaned legacy comment
    };
    // cleaned legacy comment
    const handleUpdateCourier = async () => {
        if (!selectedCourier) {
            alert('택배사를 선택해주세요.');
            return;
        }
        setIsUpdating(true);
        try {
            // cleaned legacy comment
            await Promise.all(selectedOrders.map(orderId => updateOrderShip(orderId, { ship_comp: selectedCourier })));
            // cleaned legacy comment
            alert('택배사가 성공적으로 변경되었습니다.');
            setShowCourierModal(false);
            setSelectedCourier('');
            fetchOrders(); // 주문 목록 새로고침
        }
        catch (err) {
            setError('택배사 변경 중 오류가 발생했습니다.');
            console.error('Error updating courier:', err);
        }
        finally {
            setIsUpdating(false);
        }
    };
    // cleaned legacy comment
    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig &&
            sortConfig.key === key &&
            sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };
    // cleaned legacy comment
    const getSortedData = (data) => {
        if (!sortConfig)
            return data;
        return [...data].sort((a, b) => {
            const aValue = a[sortConfig.key];
            const bValue = b[sortConfig.key];
            if (aValue === null || aValue === undefined)
                return 1;
            if (bValue === null || bValue === undefined)
                return -1;
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortConfig.direction === 'ascending'
                    ? aValue - bValue
                    : bValue - aValue;
            }
            const stringA = String(aValue).toLowerCase();
            const stringB = String(bValue).toLowerCase();
            return sortConfig.direction === 'ascending'
                ? stringA.localeCompare(stringB)
                : stringB.localeCompare(stringA);
        });
    };
    // cleaned legacy comment
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const cachedFilteredOrders = useMemo(() => getFilteredOrders(), [orders, startDate, endDate, orderStatus, paymentStatus, shippingStatus, searchTerm, searchColumn, columnFilters, filters, activeTab]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const cachedSortedOrders = useMemo(() => getSortedData(cachedFilteredOrders), [cachedFilteredOrders, sortConfig]);
    useEffect(() => {
        const hasLocalFilters = Object.values(columnFilters).some(Boolean) ||
            Object.keys(filters).length > 0;
        if (!hasLocalFilters) {
            return;
        }
        setTotalCount(cachedFilteredOrders.length);
        setCompletedCount(cachedFilteredOrders.filter((order) => order.trackingNumber || order.shippingStatus === '출고완료').length);
        setWaitingCount(cachedFilteredOrders.filter((order) => order.shippingStatus === '출고대기').length);
        setTabTotalShippingFee(cachedFilteredOrders.reduce((sum, order) => sum + order.shipCompFee * Math.max(order.quantity || 1, 1), 0));
    }, [cachedFilteredOrders, columnFilters, filters]);
    useEffect(() => {
        const hasLocalFilters = Object.values(columnFilters).some(Boolean) ||
            Object.keys(filters).length > 0;
        if (hasLocalFilters) {
            return;
        }
        setTotalCount(serverSummary.total);
        setCompletedCount(serverSummary.completed);
        setWaitingCount(serverSummary.waiting);
        setTabTotalShippingFee(serverSummary.shippingFee);
    }, [columnFilters, filters, serverSummary]);
    // cleaned legacy comment
    const getSortIcon = (key) => {
        if (!sortConfig || sortConfig.key !== key) {
            return _jsx(FiList, { className: "ms-1" });
        }
        return sortConfig.direction === 'ascending' ? (_jsx(FiArrowUp, { className: "ms-1" })) : (_jsx(FiArrowDown, { className: "ms-1" }));
    };
    // cleaned legacy comment
    const handleEditOrder = (order) => {
        setEditingOrder({ ...order });
        setShowEditModal(true);
    };
    // cleaned legacy comment
    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setEditingOrder(null);
    };
    // cleaned legacy comment
    const handleEditInputChange = (e) => {
        if (!editingOrder)
            return;
        const { name, value, type } = e.target;
        let newValue = value;
        // cleaned legacy comment
        const colDef = columns.find(col => col.key === name);
        if (colDef && colDef.isNumeric) {
            newValue = value === '' ? '' : Number(value);
        }
        setEditingOrder({ ...editingOrder, [name]: newValue });
    };
    // cleaned legacy comment
    const handleSaveEditOrder = async () => {
        if (!editingOrder)
            return;
        try {
            setLoading(true);
            // cleaned legacy comment
            let mappedStatus = undefined;
            switch (editingOrder.orderStatus) {
                case '대기':
                case '처리중':
                case '완료':
                case '취소':
                    mappedStatus = editingOrder.orderStatus;
                    break;
                default:
                    mappedStatus = undefined;
            }
            // cleaned legacy comment
            let mappedOrderType = undefined;
            switch (editingOrder.orderType) {
                case '일반주문':
                case '긴급주문':
                case '예약주문':
                case '교환':
                    mappedOrderType = editingOrder.orderType;
                    break;
                default:
                    mappedOrderType = undefined;
            }
            // cleaned legacy comment
            let mappedPaymentStatus = undefined;
            switch (editingOrder.paymentStatus) {
                case '미결제':
                case '결제완료':
                case '결제취소':
                    mappedPaymentStatus = editingOrder.paymentStatus;
                    break;
                default:
                    mappedPaymentStatus = undefined;
            }
            // cleaned legacy comment
            let mappedShippingMethod = undefined;
            switch (editingOrder.shippingMethod) {
                case '일반배송':
                case '익일배송':
                case '긴급배송':
                    mappedShippingMethod = editingOrder.shippingMethod;
                    break;
                default:
                    mappedShippingMethod = undefined;
            }
            // cleaned legacy comment
            let mappedShippingStatus = undefined;
            switch (editingOrder.shippingStatus) {
                case '대기':
                case '배송준비':
                case '배송중':
                case '배송완료':
                    mappedShippingStatus = editingOrder.shippingStatus;
                    break;
                default:
                    mappedShippingStatus = undefined;
            }
            // cleaned legacy comment
            let mappedStatusField = undefined;
            switch (editingOrder.status) {
                case '주문완료':
                case '주문취소':
                case '주문반품':
                    mappedStatusField = editingOrder.status;
                    break;
                default:
                    mappedStatusField = undefined;
            }
            // cleaned legacy comment
            const { id, ...rest } = editingOrder;
            const updateData = {
                ...rest,
                orderStatus: mappedStatus,
                orderType: mappedOrderType,
                paymentStatus: mappedPaymentStatus,
                shippingMethod: mappedShippingMethod,
                shippingStatus: mappedShippingStatus,
                status: mappedStatusField
            };
            await updateOrder(editingOrder.id, updateData);
            await fetchOrders(); // 데이터 갱신
            setShowEditModal(false); // 모달 닫기
            setEditingOrder(null);
        }
        catch (err) {
            setError('주문 수정 중 오류가 발생했습니다.');
        }
        finally {
            setLoading(false);
        }
    };
    // cleaned legacy comment
    useEffect(() => {
        const styleSheet = document.createElement("style");
        styleSheet.innerText = styles;
        document.head.appendChild(styleSheet);
        return () => {
            document.head.removeChild(styleSheet);
        };
    }, []);
    const handlePrintWbl = async (order) => {
        const targetOrderNumber = order.sapangNetOrderNumber || order.orderNumber;
        if (!targetOrderNumber) {
            alert('출력에 사용할 주문번호가 없습니다.');
            return;
        }
        const shouldContinue = await runCheonilPreflightIfNeeded([order]);
        if (!shouldContinue) {
            return;
        }
        try {
            await savePrintOrders([targetOrderNumber]);
        }
        catch (error) {
            console.error('단건 운송장 출력 주문번호 저장 실패:', error);
        }
        window.open(`/print-wbl-popup?order_numbers=${encodeURIComponent(targetOrderNumber)}`, '_blank', 'width=800,height=600');
    };
    const buildPrintPopupUrl = (orderNumbers, mode) => {
        const normalizedOrderNumbers = (orderNumbers || [])
            .map(value => String(value || '').trim())
            .filter(Boolean);
        const searchParams = new URLSearchParams();
        const serializedOrderNumbers = normalizedOrderNumbers.join(',');
        const shouldEmbedOrderNumbers = normalizedOrderNumbers.length === 1 ||
            serializedOrderNumbers.length <= 500;
        // Large batches should read from the saved print-orders store instead of
        // embedding a long query string that IIS can reject with a 404.
        if (shouldEmbedOrderNumbers && normalizedOrderNumbers.length > 0) {
            searchParams.set('order_numbers', serializedOrderNumbers);
        }
        if (mode) {
            searchParams.set('mode', mode);
        }
        const queryString = searchParams.toString();
        return queryString ? `/print-wbl-popup?${queryString}` : '/print-wbl-popup';
    };
    const buildCheonilPreflightMessage = (preflightResult, options) => {
        const summary = (preflightResult === null || preflightResult === void 0 ? void 0 : preflightResult.summary) || {};
        const issueDetails = Array.isArray(preflightResult === null || preflightResult === void 0 ? void 0 : preflightResult.issueDetails) ? preflightResult.issueDetails : [];
        const autoProceed = Boolean(options === null || options === void 0 ? void 0 : options.autoProceed);
        const lines = [
            (preflightResult === null || preflightResult === void 0 ? void 0 : preflightResult.message) || '천일 출력 사전 점검에서 이상 징후를 발견했습니다.',
            '',
            `점검 주문: ${summary.checkedCheonilOrders || 0}건`,
            `이상 주문: ${summary.issueOrders || 0}건`,
        ];
        if (summary.missingOracleOrders) {
            lines.push(`- Oracle 운송장 없음: ${summary.missingOracleOrders}건`);
        }
        if (summary.duplicateSeqTrackingOrders) {
            lines.push(`- 중복/임시 seq 송장번호: ${summary.duplicateSeqTrackingOrders}건`);
        }
        if (summary.collapsedSeqOrders) {
            lines.push(`- 묶여 있는 seq: ${summary.collapsedSeqOrders}건`);
        }
        if (summary.misalignedSeqOrders) {
            lines.push(`- Oracle 순서와 seq 불일치: ${summary.misalignedSeqOrders}건`);
        }
        if (summary.missingLinkedTrackingOrders) {
            lines.push(`- 연계된 seq 송장 누락: ${summary.missingLinkedTrackingOrders}건`);
        }
        if (summary.unexpectedSeqTrackingOrders) {
            lines.push(`- 예상 외 seq 송장 존재: ${summary.unexpectedSeqTrackingOrders}건`);
        }
        if (issueDetails.length > 0) {
            lines.push('', '이상 주문 예시:');
            issueDetails.slice(0, 10).forEach((detail) => {
                lines.push(`- ${detail.orderNumber}: ${(detail.issues || []).join(', ')}`);
            });
            if (issueDetails.length > 10) {
                lines.push(`- 외 ${issueDetails.length - 10}건`);
            }
        }
        if (preflightResult === null || preflightResult === void 0 ? void 0 : preflightResult.skippedBulkPreflight) {
            lines.push('', '대량 출력이어서 사전 점검 일부를 생략했습니다.');
        }
        lines.push('', autoProceed ? '출력은 계속 진행됩니다.' : '계속 진행하시겠습니까?');
        return lines.join('\n');
    };
    const runCheonilPreflightIfNeeded = async (selectedOrderData) => {
        const cheonilOrderNumbers = (selectedOrderData || [])
            .filter(order => order.shipComp === '천일')
            .map(order => order.sapangNetOrderNumber || order.orderNumber)
            .filter((value) => Boolean(String(value || '').trim()));
        if (cheonilOrderNumbers.length === 0) {
            return true;
        }
        try {
            const preflightResult = await preflightCheonilPrintOrders(cheonilOrderNumbers);
            if (!(preflightResult === null || preflightResult === void 0 ? void 0 : preflightResult.success)) {
                alert((preflightResult === null || preflightResult === void 0 ? void 0 : preflightResult.message) || '천일 출력 사전 점검에 실패했습니다.');
                return false;
            }
            if (!(preflightResult === null || preflightResult === void 0 ? void 0 : preflightResult.hasIssues)) {
                return true;
            }
            alert(buildCheonilPreflightMessage(preflightResult, { autoProceed: true }));
            return true;
        }
        catch (error) {
            console.error('천일 출력 사전 점검 실패:', error);
            alert('천일 출력 사전 점검 중 오류가 발생했습니다.');
            return false;
        }
    };
    const handleDeliveryTemp = async () => {
        var _a, _b, _c;
        if (selectedOrders.length === 0) {
            alert('선택된 주문이 없습니다.');
            return;
        }
        try {
            const validOrderIds = selectedOrders.filter(orderId => {
                const order = orders.find(o => o.id === orderId);
                if (!order)
                    return false;
                return !String(order.trackingNumber || '').trim();
            });
            const excludedCount = selectedOrders.length - validOrderIds.length;
            if (excludedCount > 0) {
                const proceed = window.confirm(`선택한 주문 중 ${excludedCount}건은 이미 송장번호가 있어 제외됩니다.\n나머지 ${validOrderIds.length}건만 가배송 처리할까요?`);
                if (!proceed) {
                    return;
                }
            }
            if (validOrderIds.length === 0) {
                alert('가배송 처리 대상 주문이 없습니다. (선택한 주문 모두 송장번호가 존재합니다.)');
                return;
            }
            await api.post(`/api/courier/delivery-temp`, {
                orderIds: validOrderIds
            });
            alert('가배송 주문 처리가 완료되었습니다.');
            setSelectedOrders([]);
            await fetchOrders();
        }
        catch (error) {
            const status = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status;
            const message = ((_c = (_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message) ||
                '가배송 처리 중 오류가 발생했습니다.';
            if (status !== 409) {
                console.error('가배송 처리 중 오류 발생:', error);
            }
            alert(message);
        }
    };
    const handleDeliveryImpossible = async () => {
        var _a, _b, _c, _e;
        // cleaned legacy comment
        try {
            const response = await api.get(`/api/courier/addnew-view`);
            const changedCount = (_e = (_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.changedcount) !== null && _b !== void 0 ? _b : (_c = response.data) === null || _c === void 0 ? void 0 : _c.changedCount) !== null && _e !== void 0 ? _e : 0;
            alert(`배송불가 처리된 주문 수: ${changedCount}건`);
            // cleaned legacy comment
        }
        catch (error) {
            alert('배송불가 처리 중 오류가 발생했습니다.');
        }
    };
    // cleaned legacy comment
    const handleSaveOrder = async (order) => {
        try {
            await api.post(`/api/orders/order/update-info`, {
                order_id: order.id,
                address: order.address,
                phone: order.phone,
                mobile_phone: order.mobilePhone,
                product_code: order.productCode,
                shipping_method: order.shippingMethod,
                shipping_fee: order.shippingFee,
                zip_code: order.zipCode,
                recipient_name: order.recipientName,
                message: order.message,
                boxqty: order.boxqty,
                quantity: order.quantity
            });
            alert('주문 정보가 저장되었습니다.');
            // cleaned legacy comment
        }
        catch (error) {
            alert('주문 정보 저장에 실패했습니다.');
            console.error(error);
        }
    };
    const handlePrintMultipleWbl = async () => {
        try {
            // cleaned legacy comment
            const selectedOrderData = orders.filter(order => selectedOrders.includes(order.id));
            // cleaned legacy comment
            const filteredOrders = selectedOrderData.filter(order => ['한진', '경동', '천일'].includes(order.shipComp));
            if (filteredOrders.length === 0) {
                alert('선택된 주문 중 한진, 경동, 천일 택배사 주문이 없습니다.');
                return;
            }
            const shouldContinue = await runCheonilPreflightIfNeeded(filteredOrders);
            if (!shouldContinue) {
                return;
            }
            const orderNumbers = filteredOrders
                .map(order => order.sapangNetOrderNumber || order.orderNumber)
                .filter((value) => Boolean(String(value || '').trim()));
            // cleaned legacy comment
            try {
                await savePrintOrders(orderNumbers);
            }
            catch (error) {
                console.error('주문번호 저장 실패:', error);
                // cleaned legacy comment
            }
            // cleaned legacy comment
            const printWindow = window.open(buildPrintPopupUrl(orderNumbers), '_blank', 'width=1200,height=800');
            if (!printWindow) {
                alert('팝업이 차단되었습니다. 브라우저 설정을 확인해주세요.');
            }
        }
        catch (error) {
            console.error('운송장 출력 중 오류 발생:', error);
            alert('운송장 출력 중 오류가 발생했습니다.');
        }
    };
    const handlePrintMultipleWbl2 = async () => {
        try {
            // cleaned legacy comment
            const selectedOrderData = orders.filter(order => selectedOrders.includes(order.id));
            // cleaned legacy comment
            const filteredOrders = selectedOrderData.filter(order => ['경동', '천일'].includes(order.shipComp));
            if (filteredOrders.length === 0) {
                alert('선택된 주문 중 경동, 천일 택배사 주문이 없습니다.');
                return;
            }
            const shouldContinue = await runCheonilPreflightIfNeeded(filteredOrders);
            if (!shouldContinue) {
                return;
            }
            const orderNumbers = filteredOrders
                .map(order => order.sapangNetOrderNumber || order.orderNumber)
                .filter((value) => Boolean(String(value || '').trim()));
            // cleaned legacy comment
            try {
                await savePrintOrders(orderNumbers);
            }
            catch (error) {
                console.error('주문번호 저장 실패:', error);
                // cleaned legacy comment
            }
            // cleaned legacy comment
            const printWindow = window.open(buildPrintPopupUrl(orderNumbers, 'reissue'), '_blank', 'width=1200,height=800');
            if (!printWindow) {
                alert('팝업이 차단되었습니다. 브라우저 설정을 확인해주세요.');
            }
        }
        catch (error) {
            console.error('운송장 출력 중 오류 발생:', error);
            alert('운송장 출력 중 오류가 발생했습니다.');
        }
    };
    // cleaned legacy comment
    const handleHanjinReissueBridge = async () => {
        try {
            const selectedOrderData = orders.filter(order => selectedOrders.includes(order.id));
            const filteredOrders = selectedOrderData.filter(order => ['한진', '경동', '천일'].includes(order.shipComp));
            if (filteredOrders.length === 0) {
                alert('선택된 주문 중 한진, 경동, 천일 택배사 주문이 없습니다.');
                return;
            }
            const shouldContinue = await runCheonilPreflightIfNeeded(filteredOrders);
            if (!shouldContinue) {
                return;
            }
            const orderNumbers = filteredOrders
                .map(order => order.sapangNetOrderNumber || order.orderNumber)
                .filter((value) => Boolean(String(value || '').trim()));
            try {
                await savePrintOrders(orderNumbers);
            }
            catch (error) {
                console.error('주문번호 저장 실패:', error);
            }
            const printWindow = window.open(buildPrintPopupUrl(orderNumbers, 'reissue'), '_blank', 'width=1200,height=800');
            if (!printWindow) {
                alert('팝업이 차단되었습니다. 브라우저 설정을 확인해주세요.');
            }
        }
        catch (error) {
            console.error('운송장 재발행 중 오류 발생:', error);
            alert('운송장 재발행 중 오류가 발생했습니다.');
        }
    };
    const fetchRemainCounts = async (productCodes) => {
        try {
            const response = await api.post('/api/locations/remain-counts', { productCodes });
            setRemainCounts(response.data);
        }
        catch (error) {
            setRemainCounts({});
        }
    };
    // cleaned legacy comment
    useEffect(() => {
        const codes = Array.from(new Set(orders.map(order => order.productCode)));
        if (codes.length > 0)
            fetchRemainCounts(codes);
    }, [orders]);
    // cleaned legacy comment
    const handleCancelTracking = async () => {
        if (selectedOrders.length === 0) {
            alert('취소할 주문을 선택해주세요.');
            return;
        }
        if (!window.confirm(`선택한 ${selectedOrders.length}개 주문의 송장을 취소하시겠습니까?\n\n주의: 아래 변경사항이 함께 적용됩니다.\n- 운송장번호 삭제\n- 출고 기록 삭제\n- 재고 복구`)) {
            return;
        }
        setLoading(true);
        try {
            const response = await api.post('/api/orders/cancel-tracking', {
                orderIds: selectedOrders
            });
            const result = response.data;
            if (result.success) {
                alert(`송장 취소가 완료되었습니다.\n처리된 주문: ${result.processedCount}건`);
                setSelectedOrders([]);
                await fetchOrders(); // 목록 새로고침
            }
            else {
                throw new Error(result.message || '송장 취소 중 오류가 발생했습니다.');
            }
        }
        catch (error) {
            console.error('송장 취소 오류:', error);
            alert(`송장 취소 중 오류가 발생했습니다: ${error instanceof Error ? error.message : '알 수 없는 오류'}`);
        }
        finally {
            setLoading(false);
        }
    };
    // cleaned legacy comment
    const handleValidateStock = async () => {
        var _a;
        try {
            const validateDate = startDate || endDate || new Date().toISOString().split('T')[0];
            const response = await api.get(`/api/orders/validate-stock?date=${validateDate}`, { responseType: 'blob' });
            const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', `재고검증_부족분_${validateDate}.xlsx`);
            document.body.appendChild(link);
            link.click();
            (_a = link.parentNode) === null || _a === void 0 ? void 0 : _a.removeChild(link);
        }
        catch (error) {
            console.error('재고 검증 오류:', error);
            alert('재고 검증 중 오류가 발생했습니다.');
        }
    };
    const handleCancelOrderCheck = async () => {
        if (!window.confirm('사방넷에서 취소 주문을 확인하시겠습니까?\n\n사방넷 API를 호출하여 취소 주문을 조회하고 tb_orders 테이블의 상태를 취소로 업데이트합니다.')) {
            return;
        }
        setLoading(true);
        try {
            const cancelCheckStartDate = startDate || endDate;
            const cancelCheckEndDate = endDate || startDate;
            if (!cancelCheckStartDate || !cancelCheckEndDate) {
                throw new Error('조회 기간이 설정되지 않았습니다.');
            }
            const response = await api.get(`/api/sabangnet/cancelorder?startDate=${cancelCheckStartDate}&endDate=${cancelCheckEndDate}`);
            const result = response.data;
            if (result.success) {
                alert(`취소 주문 확인이 완료되었습니다.\n\n처리 결과:\n\n- 취소 주문: ${result.cancelOrders}건\n- 처리 완료: ${result.processedCount}건\n- 찾지 못한 주문: ${result.notFoundCount}건`);
                await fetchOrders(); // 목록 새로고침
            }
            else {
                throw new Error(result.message || '취소 주문 확인 중 오류가 발생했습니다.');
            }
        }
        catch (error) {
            console.error('취소 주문 확인 오류:', error);
            alert(`취소 주문 확인 중 오류가 발생했습니다: ${error instanceof Error ? error.message : '알 수 없는 오류'}`);
        }
        finally {
            setLoading(false);
        }
    };
    // cleaned legacy comment
    const clearAllFilters = () => {
        const _t = new Date();
        const todayStr = `${_t.getFullYear()}-${String(_t.getMonth() + 1).padStart(2, '0')}-${String(_t.getDate()).padStart(2, '0')}`;
        setStartDate(todayStr);
        setEndDate(todayStr);
        setOrderStatus('');
        setPaymentStatus('');
        setShippingStatus('');
        setSearchTerm('');
        setSearchColumn('recipientName');
        setShippingStatus('');
        setFilters({});
        setColumnFilters(initialFilters);
        setCurrentPage(1);
        setSearchTrigger(t => t + 1);
    };
    const activateTab = (tab) => {
        setActiveTab(tab);
        setSelectedOrders([]);
        setCurrentPage(1);
        setFilters({});
        setColumnFilters(initialFilters);
        setSearchTrigger((t) => t + 1);
    };
    return (_jsxs("div", { className: "container-fluid", children: [_jsx("div", { className: "row mb-3", children: _jsx("div", { className: "col-12" }) }), _jsxs("div", { className: "allocation-top-layout flex", style: { justifyContent: 'space-between' }, children: [_jsx("div", { className: "row mb-1 allocation-summary-strip", style: { width: '50%' }, children: _jsxs("div", { className: "col-12 d-flex justify-content-end", children: [_jsx("div", { className: "card me-1 allocation-summary-card", style: { flex: '40%' }, children: _jsx("div", { className: "card-body py-0 px-2", children: _jsxs("div", { className: "d-flex align-items-center", children: [_jsx("div", { className: "flex-shrink-0", children: _jsx("div", { className: "avatar-sm rounded-circle bg-primary allocation-summary-badge", children: _jsx("i", { className: "avatar-title text-white", children: "\uC804\uCCB4" }) }) }), _jsxs("div", { className: "flex-grow-1 ms-1", children: [_jsx("h4", { className: "mt-0 mb-0 fs-5", children: totalCount }), _jsx("p", { className: "text-muted mb-0 fs-6", children: "\uC804\uCCB4 \uC8FC\uBB38" })] })] }) }) }), _jsx("div", { className: "card me-1 allocation-summary-card", style: { flex: '40%' }, children: _jsx("div", { className: "card-body py-0 px-2", children: _jsxs("div", { className: "d-flex align-items-center", children: [_jsx("div", { className: "flex-shrink-0", children: _jsx("div", { className: "avatar-sm rounded-circle bg-success allocation-summary-badge", children: _jsx("i", { className: "avatar-title text-white", children: "\uC644\uB8CC" }) }) }), _jsxs("div", { className: "flex-grow-1 ms-1", children: [_jsx("h4", { className: "mt-0 mb-0 fs-5", children: completedCount }), _jsx("p", { className: "text-muted mb-0 fs-6", children: "\uCC98\uB9AC \uC644\uB8CC" })] })] }) }) }), _jsx("div", { className: "card me-1 allocation-summary-card", style: { flex: '40%' }, children: _jsx("div", { className: "card-body py-0 px-2", children: _jsxs("div", { className: "d-flex align-items-center", children: [_jsx("div", { className: "flex-shrink-0", children: _jsx("div", { className: "avatar-sm rounded-circle bg-warning allocation-summary-badge", children: _jsx("i", { className: "avatar-title text-white", children: "\uB300\uAE30" }) }) }), _jsxs("div", { className: "flex-grow-1 ms-1", children: [_jsx("h4", { className: "mt-0 mb-0 fs-5", children: waitingCount }), _jsx("p", { className: "text-muted mb-0 fs-6", children: "\uCC98\uB9AC \uB300\uAE30" })] })] }) }) })] }) }), _jsx("div", { className: "row mb-1 allocation-actions-wrap", children: _jsx("div", { className: "col-12", children: _jsx("div", { className: "card allocation-actions-card", children: _jsx("div", { className: "card-body py-2", children: _jsxs("div", { className: "d-flex flex-wrap gap-2", children: [_jsxs("button", { type: "button", className: "btn btn-success me-2 allocation-action-btn", style: { backgroundColor: '#28a745', color: '#fff', border: 'none' }, onClick: () => handleExcelDownload(), children: [_jsx(FiDownload, { className: "me-1" }), " \uC5D1\uC140 \uB2E4\uC6B4\uB85C\uB4DC"] }), _jsxs("button", { type: "button", className: "btn btn-info me-2 allocation-action-btn", style: { backgroundColor: '#17a2b8', color: '#fff', border: 'none' }, onClick: () => handleLocationPickingExcel(), disabled: loading, children: [_jsx(FiDownload, { className: "me-1" }), " ", loading ? '생성 중...' : '로케이션별 피킹리스트'] }), _jsxs("button", { type: "button", className: "btn btn-success me-2 allocation-action-btn", style: { backgroundColor: '#28a745', color: '#fff', border: 'none' }, onClick: () => handleBundleTransferSeparated(), children: [_jsx(FiUpload, { className: "me-1" }), " \uD569\uBC30\uC1A1 \uC804\uC1A1"] }), _jsxs("button", { type: "button", className: "btn btn-success me-2 allocation-action-btn", style: { backgroundColor: '#28a745', color: '#fff', border: 'none' }, onClick: () => handleRegularCourierTransferSeparated(), children: [_jsx(FiUpload, { className: "me-1" }), " \uD0DD\uBC30\uC0AC \uC804\uC1A1"] }), _jsxs("button", { type: "button", className: "btn btn-info me-2 allocation-action-btn", style: { backgroundColor: '#17a2b8', color: '#fff', border: 'none' }, onClick: () => handleSabangnetTransfer(), children: [_jsx(FiUpload, { className: "me-1" }), " \uC0AC\uBC29\uB137 \uC804\uC1A1"] }), _jsxs("button", { type: "button", className: "btn btn-info me-2 allocation-action-btn", style: { backgroundColor: '#17a2b8', color: '#fff', border: 'none' }, onClick: handleSabangnetCheck, children: [_jsx(FiUpload, { className: "me-1" }), " \uC0AC\uBC29\uB137 \uD655\uC778"] }), _jsxs("button", { type: "button", className: "btn btn-info me-2 allocation-action-btn", style: { backgroundColor: '#17a2b8', color: '#fff', border: 'none' }, onClick: () => handlePrintMultipleWbl(), children: [_jsx(FiUpload, { className: "me-1" }), " \uC6B4\uC1A1\uC7A5 \uCD9C\uB825"] }), _jsxs("button", { type: "button", className: "btn btn-info me-2 allocation-action-btn", style: { backgroundColor: '#17a2b8', color: '#fff', border: 'none' }, onClick: () => handleHanjinReissueBridge(), children: [_jsx(FiUpload, { className: "me-1" }), " \uC6B4\uC1A1\uC7A5 \uC7AC\uBC1C\uD589"] }), _jsxs("button", { type: "button", className: "btn btn-warning me-2 allocation-action-btn", style: { backgroundColor: '#ffc107', color: '#212529', border: 'none' }, onClick: () => handleCourierMove(), children: [_jsx(FiArrowLeft, { className: "me-1" }), " \uD0DD\uBC30\uC0AC \uC774\uB3D9"] }), _jsxs("button", { type: "button", className: "btn btn-danger me-2 allocation-action-btn", style: { backgroundColor: '#dc3545', color: '#fff', border: 'none' }, onClick: () => handleDeliveryImpossible(), children: [_jsx(FiX, { className: "me-1" }), " \uBC30\uC1A1\uBD88\uAC00"] }), _jsxs("button", { type: "button", className: "btn btn-danger me-2 allocation-action-btn", style: { backgroundColor: '#dc3545', color: '#fff', border: 'none' }, onClick: () => handleDeliveryTemp(), children: [_jsx(FiX, { className: "me-1" }), " \uAC00\uBC30\uC1A1"] }), _jsxs("button", { type: "button", className: "btn btn-danger me-2 allocation-action-btn", style: { backgroundColor: '#dc3545', color: '#fff', border: 'none' }, onClick: () => handleCancelTracking(), disabled: loading || selectedOrders.length === 0, children: [_jsx(FiX, { className: "me-1" }), " \uC1A1\uC7A5\uCDE8\uC18C"] }), _jsxs("button", { type: "button", className: "btn btn-warning me-2 allocation-action-btn", style: { backgroundColor: '#fd7e14', color: '#fff', border: 'none' }, onClick: () => handleCancelOrderCheck(), disabled: loading, children: [_jsx(FiCheck, { className: "me-1" }), " \uCDE8\uC18C\uC8FC\uBB38\uD655\uC778"] }), _jsxs("button", { type: "button", className: "btn btn-warning me-2 allocation-action-btn", style: { backgroundColor: '#20c997', color: '#fff', border: 'none' }, onClick: () => handleValidateStock(), disabled: loading, children: [_jsx(FiCheck, { className: "me-1" }), " \uC7AC\uACE0 \uAC80\uC99D"] })] }) }) }) }) })] }), _jsx("div", { className: "row mb-1", children: _jsx("div", { className: "col-12", children: _jsx("div", { className: "card allocation-tabs-card", children: _jsx("div", { className: "card-body py-2", children: _jsxs("div", { className: "d-flex justify-content-between align-items-center", children: [_jsxs("ul", { className: "nav nav-tabs nav-tabs-custom", style: { flex: 1 }, children: [_jsx("li", { className: "nav-item", children: _jsxs("a", { className: `nav-link tab-all ${activeTab === 'all' ? 'active' : ''}`, onClick: () => activateTab('all'), style: { cursor: 'pointer' }, children: ["\uC804\uCCB4 ", _jsx("span", { className: "badge bg-primary ms-1", children: getTabCount('all') })] }) }), _jsx("li", { className: "nav-item", children: _jsxs("a", { className: `nav-link tab-confirm-request ${activeTab === 'confirm_request' ? 'active' : ''}`, onClick: () => activateTab('confirm_request'), style: { cursor: 'pointer' }, children: ["\uD655\uC778\uC694\uCCAD ", _jsx("span", { className: "badge bg-warning ms-1", children: getTabCount('confirm_request') })] }) }), _jsx("li", { className: "nav-item", children: _jsxs("a", { className: `nav-link tab-high-fee ${activeTab === 'high_fee' ? 'active' : ''}`, onClick: () => activateTab('high_fee'), style: { cursor: 'pointer' }, children: ["\uBC30\uC1A1\uBE44 \uCD08\uACFC ", _jsx("span", { className: "badge bg-primary ms-1", children: getTabCount('high_fee') })] }) }), _jsx("li", { className: "nav-item", children: _jsxs("a", { className: `nav-link tab-common ${activeTab === 'common' ? 'active' : ''}`, onClick: () => activateTab('common'), style: { cursor: 'pointer' }, children: ["\uACF5\uC6A9 ", _jsx("span", { className: "badge bg-primary ms-1", children: getTabCount('common') })] }) }), _jsx("li", { className: "nav-item", children: _jsxs("a", { className: `nav-link tab-cargo ${activeTab === 'cargo' ? 'active' : ''}`, onClick: () => activateTab('cargo'), style: { cursor: 'pointer' }, children: ["\uD654\uBB3C ", _jsx("span", { className: "badge bg-primary ms-1", children: getTabCount('cargo') })] }) }), _jsx("li", { className: "nav-item", children: _jsxs("a", { className: `nav-link tab-jeju-island ${activeTab === 'jeju_island' ? 'active' : ''}`, onClick: () => activateTab('jeju_island'), style: { cursor: 'pointer' }, children: ["\uC81C\uC8FC, \uB3C4\uC11C\uC0B0\uAC04 ", _jsx("span", { className: "badge bg-primary ms-1", children: getTabCount('jeju_island') })] }) }), getCourierCompanies().map(company => (_jsx("li", { className: "nav-item", children: _jsxs("a", { className: `nav-link tab-courier ${activeTab === company ? 'active' : ''}`, onClick: () => activateTab(company), style: { cursor: 'pointer' }, children: [company || '미지정', " ", _jsx("span", { className: "badge bg-primary ms-1", children: getTabCount(company) })] }) }, company))), getAccessoryCourierCompanies().map(company => {
                                                const accessoryTabKey = `${ACCESSORY_TAB_PREFIX}${company}`;
                                                return (_jsx("li", { className: "nav-item", children: _jsxs("a", { className: `nav-link tab-courier ${activeTab === accessoryTabKey ? 'active' : ''}`, onClick: () => activateTab(accessoryTabKey), style: { cursor: 'pointer' }, children: [`${normalizeCourierName(company)}(부속)`, " ", _jsx("span", { className: "badge bg-primary ms-1", children: getTabCount(accessoryTabKey) })] }) }, accessoryTabKey));
                                            })] }), _jsxs("div", { className: "fw-bold allocation-total-fee", style: { minWidth: 220, textAlign: 'right', fontSize: '1.1rem' }, children: ["\uCD1D\uBC30\uC1A1\uBE44 \uD569\uACC4 : ", getTabTotalShippingFee(activeTab).toLocaleString(), "\uC6D0"] })] }) }) }) }) }), error && (_jsx("div", { className: "row mb-1", children: _jsx("div", { className: "col-12", children: _jsx("div", { className: "alert alert-danger py-2", role: "alert", children: error }) }) })), _jsx("div", { className: "bg-white rounded-lg border border-gray-300 shadow-sm p-3 mb-3 allocation-filter-card", children: _jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsxs("div", { className: "flex items-center gap-1 flex-wrap", children: [_jsx("label", { className: "text-sm font-bold whitespace-nowrap", children: "\uAE30\uAC04:" }), _jsx("input", { type: "date", className: "h-9 px-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500", value: startDate, onChange: (e) => setStartDate(e.target.value), style: { width: '135px' } }), _jsx("span", { className: "text-sm text-gray-500", children: "~" }), _jsx("input", { type: "date", className: "h-9 px-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500", value: endDate, onChange: (e) => setEndDate(e.target.value), style: { width: '135px' } }), _jsx("div", { className: "flex gap-1", children: ['today', 'yesterday', 'week', 'month1', 'month3'].map((t, i) => (_jsx("button", { onClick: () => setQuickDate(t), className: "h-9 px-2 text-xs border border-gray-300 rounded-md hover:bg-blue-50 hover:border-blue-400 hover:text-blue-600 whitespace-nowrap transition-colors", children: ['오늘', '전일', '일주일', '1개월', '3개월'][i] }, t))) })] }), _jsxs("div", { className: "flex items-center gap-1", children: [_jsx("label", { className: "text-sm font-bold whitespace-nowrap", children: "\uC8FC\uBB38\uC0C1\uD0DC:" }), _jsxs("select", { className: "h-9 px-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500", value: orderStatus, onChange: (e) => setOrderStatus(e.target.value), style: { width: '110px' }, children: [_jsx("option", { value: "", children: "\uC804\uCCB4" }), _jsx("option", { value: "\uC8FC\uBB38\uC811\uC218", children: "\uC8FC\uBB38\uC811\uC218" }), _jsx("option", { value: "\uACB0\uC81C\uC644\uB8CC", children: "\uACB0\uC81C\uC644\uB8CC" }), _jsx("option", { value: "\uC0C1\uD488\uC900\uBE44\uC911", children: "\uC0C1\uD488\uC900\uBE44\uC911" }), _jsx("option", { value: "\uBC30\uC1A1\uC900\uBE44\uC911", children: "\uBC30\uC1A1\uC900\uBE44\uC911" }), _jsx("option", { value: "\uBC30\uC1A1\uC911", children: "\uBC30\uC1A1\uC911" }), _jsx("option", { value: "\uBC30\uC1A1\uC644\uB8CC", children: "\uBC30\uC1A1\uC644\uB8CC" }), _jsx("option", { value: "\uC8FC\uBB38\uCDE8\uC18C", children: "\uC8FC\uBB38\uCDE8\uC18C" })] })] }), _jsxs("div", { className: "flex items-center gap-1", children: [_jsx("label", { className: "text-sm font-bold whitespace-nowrap", children: "\uACB0\uC81C\uC0C1\uD0DC:" }), _jsxs("select", { className: "h-9 px-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500", value: paymentStatus, onChange: (e) => setPaymentStatus(e.target.value), style: { width: '100px' }, children: [_jsx("option", { value: "", children: "\uC804\uCCB4" }), _jsx("option", { value: "\uACB0\uC81C\uB300\uAE30", children: "\uACB0\uC81C\uB300\uAE30" }), _jsx("option", { value: "\uACB0\uC81C\uC644\uB8CC", children: "\uACB0\uC81C\uC644\uB8CC" }), _jsx("option", { value: "\uBD80\uBD84\uD658\uBD88", children: "\uBD80\uBD84\uD658\uBD88" }), _jsx("option", { value: "\uC804\uCCB4\uD658\uBD88", children: "\uC804\uCCB4\uD658\uBD88" })] })] }), _jsxs("div", { className: "flex items-center gap-1", children: [_jsx("label", { className: "text-sm font-bold whitespace-nowrap", children: "\uBC30\uC1A1\uC0C1\uD0DC:" }), _jsxs("select", { className: "h-9 px-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500", value: shippingStatus, onChange: (e) => setShippingStatus(e.target.value), style: { width: '100px' }, children: [_jsx("option", { value: "", children: "\uC804\uCCB4" }), _jsx("option", { value: "\uCD9C\uACE0\uB300\uAE30", children: "\uCD9C\uACE0\uB300\uAE30" }), _jsx("option", { value: "\uCD9C\uACE0\uC644\uB8CC", children: "\uCD9C\uACE0\uC644\uB8CC" }), _jsx("option", { value: "\uBC30\uC1A1\uC900\uBE44", children: "\uBC30\uC1A1\uC900\uBE44" }), _jsx("option", { value: "\uBC30\uC1A1\uC900\uBE44\uC911", children: "\uBC30\uC1A1\uC900\uBE44\uC911" }), _jsx("option", { value: "\uBC30\uC1A1\uC911", children: "\uBC30\uC1A1\uC911" }), _jsx("option", { value: "\uBC30\uC1A1\uC644\uB8CC", children: "\uBC30\uC1A1\uC644\uB8CC" })] })] }), _jsxs("div", { className: "flex items-center gap-1 flex-1 min-w-0", children: [_jsxs("select", { className: "h-9 px-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500", value: searchColumn, onChange: (e) => setSearchColumn(e.target.value), style: { width: '100px' }, children: [_jsx("option", { value: "recipientName", children: "\uC218\uCDE8\uC778\uBA85" }), _jsx("option", { value: "id", children: "\uC8FC\uBB38\uBC88\uD638" }), _jsx("option", { value: "productName", children: "\uC0C1\uD488\uBA85" })] }), _jsx("input", { type: "text", className: "h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-0 flex-1", placeholder: "\uAC80\uC0C9\uC5B4\uB97C \uC785\uB825\uD558\uC138\uC694", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), onKeyDown: (e) => e.key === 'Enter' && handleSearch(), style: { maxWidth: '200px' } }), _jsxs("button", { className: "h-9 px-4 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 whitespace-nowrap flex items-center gap-1", onClick: handleSearch, children: [_jsx(FiSearch, { size: 14 }), " \uAC80\uC0C9"] }), _jsxs("button", { className: "h-9 px-4 border border-gray-300 text-sm rounded-md hover:bg-gray-50 whitespace-nowrap flex items-center gap-1", onClick: clearAllFilters, children: [_jsx(FiRefreshCw, { size: 14 }), " \uCD08\uAE30\uD654"] }), Object.keys(filters).length > 0 && (_jsxs("button", { className: "h-9 px-3 border border-orange-300 text-orange-600 text-sm rounded-md hover:bg-orange-50 whitespace-nowrap", onClick: () => { setFilters({}); setColumnFilters(initialFilters); }, children: ["\uCEEC\uB7FC\uD544\uD130 \uD574\uC81C (", Object.keys(filters).length, ")"] }))] })] }) }), _jsx("div", { className: "row", children: _jsx("div", { className: "col-12", children: _jsx("div", { className: "card allocation-table-card", children: _jsxs("div", { className: "card-body allocation-table-body", children: [_jsx("div", { className: "table-responsive allocation-table-responsive", style: { maxHeight: '700px', overflowX: 'auto', overflowY: 'auto', width: '100%' }, children: _jsxs("table", { className: "table table-bordered table-hover table-sm allocation-table", style: { fontSize: '0.9rem', whiteSpace: 'nowrap', width: '100%' }, children: [_jsxs("thead", { style: { position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 3 }, children: [_jsxs("tr", { style: { height: '30px' }, children: [_jsx("th", { style: { padding: '4px 8px', minWidth: '110px', position: 'sticky', left: 0, backgroundColor: '#fff', zIndex: 5 }, children: "\uAD00\uB9AC" }), _jsx("th", { style: { position: 'sticky', left: '110px', backgroundColor: '#fff', zIndex: 4, minWidth: '40px', padding: '4px 8px' }, children: _jsxs("div", { className: "form-check", children: [_jsx("input", { type: "checkbox", className: "form-check-input", id: "selectAllOrders", onChange: handleSelectAll }), _jsx("label", { className: "form-check-label", htmlFor: "selectAllOrders" })] }) }), columns.map(col => (_jsx("th", { onClick: () => requestSort(col.key), className: `cursor-pointer${col.isNumeric ? ' text-end' : ''}`, style: { minWidth: '100px', padding: '4px 8px' }, children: _jsxs(Space, { children: [col.label, _jsx(Dropdown, { menu: { items: getFilterMenuItems(col.key, orders) }, trigger: ['click'], children: _jsx(FilterFilled, { style: {
                                                                                    color: filters[col.key] ? '#1890ff' : '#bfbfbf',
                                                                                    cursor: 'pointer'
                                                                                }, onClick: (e) => e.stopPropagation() }) }), getSortIcon(col.key)] }) }, col.key)))] }), _jsxs("tr", { children: [_jsx("th", {}), _jsx("th", {}), columns.map(col => (_jsx("th", { children: _jsx("input", { type: "text", className: "form-control form-control-sm", style: { minWidth: '80px' }, value: columnFilters[col.key], onChange: e => handleColumnFilterChange(col.key, e.target.value), placeholder: "\uAC80\uC0C9" }) }, col.key)))] })] }), _jsx("tbody", { children: loading ? (_jsx("tr", { style: { height: '30px' }, children: _jsx("td", { colSpan: 33, className: "text-center py-2", children: _jsx("div", { className: "spinner-border spinner-border-sm", role: "status", children: _jsx("span", { className: "visually-hidden", children: "\uB85C\uB529 \uC911..." }) }) }) })) : cachedSortedOrders.length > 0 ? (cachedSortedOrders.map((order) => (_jsxs("tr", { style: {
                                                        backgroundColor: highlightProductCodes.has(order.productCode)
                                                            ? '#87CEEB' // 하늘색 배경
                                                            : order.delivery_area_type && order.delivery_area_type !== 'N'
                                                                ? '#ffeb3b'
                                                                : order.shipComp === '-'
                                                                    ? '#fff3e0'
                                                                    : 'inherit',
                                                        height: '30px',
                                                        // cleaned legacy comment
                                                        border: (!order.mobilePhone || order.mobilePhone.trim() === '' || order.boxqty === 0 || (order.address && order.address.length >= 50))
                                                            ? '2px solid #dc3545'
                                                            : 'inherit'
                                                    }, children: [_jsx("td", { style: {
                                                                padding: '4px 8px',
                                                                minWidth: '110px',
                                                                position: 'sticky',
                                                                left: 0,
                                                                backgroundColor: highlightProductCodes.has(order.productCode)
                                                                    ? '#87CEEB' // 하늘색 배경
                                                                    : order.delivery_area_type && order.delivery_area_type !== 'N'
                                                                        ? '#ffeb3b'
                                                                        : order.shipComp === '-'
                                                                            ? '#fff3e0'
                                                                            : '#fff',
                                                                zIndex: 3
                                                            }, children: _jsxs("div", { className: "d-flex gap-1", children: [_jsx("button", { type: "button", className: "btn btn-sm btn-outline-primary", onClick: () => handleEditOrder(order), title: "\uC218\uC815", children: _jsx(FiEdit, {}) }), _jsx("button", { type: "button", className: "btn btn-sm btn-outline-danger", onClick: () => handleDeleteOrder(order.id), title: "\uC0AD\uC81C", children: _jsx(FiTrash2, {}) }), _jsx("button", { onClick: () => handlePrintWbl(order), children: "\uC6B4\uC1A1\uC7A5 \uCD9C\uB825" })] }) }), _jsx("td", { style: {
                                                                position: 'sticky',
                                                                left: '110px',
                                                                backgroundColor: highlightProductCodes.has(order.productCode)
                                                                    ? '#87CEEB' // 하늘색 배경
                                                                    : order.delivery_area_type && order.delivery_area_type !== 'N'
                                                                        ? '#ffeb3b'
                                                                        : order.shipComp === '-'
                                                                            ? '#fff3e0'
                                                                            : '#fff',
                                                                zIndex: 2,
                                                                padding: '4px 8px'
                                                            }, children: _jsxs("div", { className: "form-check", children: [_jsx("input", { type: "checkbox", className: "form-check-input", id: `order-${order.id}`, checked: selectedOrders.includes(order.id), onChange: () => handleSelectOrder(order.id) }), _jsx("label", { className: "form-check-label", htmlFor: `order-${order.id}` })] }) }), columns.map(col => {
                                                            var _a, _b, _c;
                                                            return (col.key === 'zipCode' ? (_jsx("td", { className: `${col.isNumeric ? 'text-end' : ''} ` +
                                                                    ((order.zipCode && order.zipCode.replace(/\D/g, '').length !== 5) ? 'text-danger' : ''), style: { minWidth: '100px', padding: '4px 8px' }, children: (_a = order.zipCode) !== null && _a !== void 0 ? _a : '' }, col.key)) : col.key === 'productCode' ? (_jsx("td", { className: col.isNumeric ? 'text-end' : '', style: {
                                                                    minWidth: '100px',
                                                                    padding: '4px 8px',
                                                                    color: remainCounts[order.productCode] !== undefined && remainCounts[order.productCode] <= 0 ? 'red' : undefined
                                                                }, children: (_b = order.productCode) !== null && _b !== void 0 ? _b : '' }, col.key)) : (_jsx("td", { className: col.isNumeric ? 'text-end' : '', style: { minWidth: '100px', padding: '4px 8px' }, children: col.isNumeric
                                                                    ? (order[col.key] !== undefined && order[col.key] !== null
                                                                        ? Number(order[col.key]).toLocaleString()
                                                                        : '')
                                                                    : ((_c = order[col.key]) !== null && _c !== void 0 ? _c : '') }, col.key)));
                                                        })] }, order.id)))) : (_jsx("tr", { style: { height: '30px' }, children: _jsx("td", { colSpan: 33, className: "text-center py-2", children: "\uB4F1\uB85D\uB41C \uC8FC\uBB38\uC774 \uC5C6\uC2B5\uB2C8\uB2E4." }) })) })] }) }), _jsxs("div", { className: "mt-2", children: [_jsx("div", { className: "d-flex justify-content-center w-100", children: cachedFilteredOrders.length > 0 && (_jsxs("div", { className: "d-flex align-items-center", children: [_jsx("button", { className: "btn btn-sm btn-outline-secondary me-1", onClick: () => handlePageChange(1), disabled: itemsPerPage === 0 || currentPage === 1, children: "\uC774\uC804" }), _jsx("div", { className: "px-3 py-1 mx-1 border rounded", children: itemsPerPage === 0 ? '1 / 1' : `${currentPage} / ${Math.ceil(totalCount / itemsPerPage)}` }), _jsx("button", { className: "btn btn-sm btn-outline-secondary ms-1", onClick: () => handlePageChange(currentPage + 1), disabled: itemsPerPage === 0 || currentPage >= Math.ceil(totalCount / itemsPerPage), children: "\uB2E4\uC74C" }), _jsxs("select", { className: "form-select form-select-sm ms-3", style: { width: '80px' }, value: itemsPerPage, onChange: (e) => {
                                                            setItemsPerPage(Number(e.target.value));
                                                            setCurrentPage(1);
                                                        }, children: [_jsx("option", { value: "10", children: "10\uAC1C" }), _jsx("option", { value: "20", children: "20\uAC1C" }), _jsx("option", { value: "50", children: "50\uAC1C" }), _jsx("option", { value: "100", children: "100\uAC1C" }), _jsx("option", { value: "0", children: "\uC804\uCCB4" })] })] })) }), _jsxs("div", { className: "d-flex justify-content-between w-100 mt-2", children: [_jsxs("div", { children: [_jsxs("button", { className: "btn btn-danger btn-sm me-2", disabled: selectedOrders.length === 0, onClick: () => {
                                                                if (window.confirm(`${selectedOrders.length}개의 주문을 삭제하시겠습니까?`)) {
                                                                    selectedOrders.forEach(id => handleDeleteOrder(id));
                                                                    setSelectedOrders([]);
                                                                }
                                                            }, children: [_jsx(FiTrash2, { className: "me-1" }), " \uC120\uD0DD \uC0AD\uC81C"] }), _jsxs("span", { className: "text-muted small ms-2", children: ["\uC120\uD0DD: ", selectedOrders.length, "\uAC1C"] })] }), _jsx("div", { children: _jsxs("span", { className: "text-muted small", children: ["\uCD1D ", totalCount, "\uAC1C\uC758 \uC8FC\uBB38"] }) })] })] })] }) }) }) }), showCourierModal && (_jsxs("div", { children: [_jsx("div", { className: "modal fade show", style: { display: 'block' }, children: _jsx("div", { className: "modal-dialog", style: { width: '80%', marginLeft: '2cm' }, children: _jsxs("div", { className: "modal-content", children: [_jsxs("div", { className: "modal-header", children: [_jsx("h5", { className: "modal-title", children: "\uD0DD\uBC30\uC0AC \uBCC0\uACBD" }), _jsx("button", { type: "button", className: "btn-close", onClick: () => setShowCourierModal(false), disabled: isUpdating })] }), _jsxs("div", { className: "modal-body", children: [_jsxs("div", { className: "mb-3", children: [_jsx("label", { className: "form-label", children: "\uD0DD\uBC30\uC0AC \uC120\uD0DD" }), _jsxs("select", { className: "form-select", value: selectedCourier, onChange: (e) => setSelectedCourier(e.target.value), disabled: isUpdating, children: [_jsx("option", { value: "", children: "\uD0DD\uBC30\uC0AC \uC120\uD0DD" }), _jsx("option", { value: "\uACBD\uB3D9", children: "\uACBD\uB3D9" }), _jsx("option", { value: "\uCC9C\uC77C", children: "\uCC9C\uC77C" }), _jsx("option", { value: "\uD55C\uC9C4", children: "\uD55C\uC9C4" }), _jsx("option", { value: "\uACF5\uC6A9", children: "\uACF5\uC6A9" }), _jsx("option", { value: "\uD654\uBB3C", children: "\uD654\uBB3C" })] })] }), _jsxs("div", { className: "alert alert-info", children: ["\uC120\uD0DD\uB41C \uC8FC\uBB38 ", selectedOrders.length, "\uAC74\uC758 \uD0DD\uBC30\uC0AC\uB97C \uBCC0\uACBD\uD569\uB2C8\uB2E4."] })] }), _jsxs("div", { className: "modal-footer", children: [_jsx("button", { type: "button", className: "btn btn-secondary", onClick: () => setShowCourierModal(false), disabled: isUpdating, children: "\uCDE8\uC18C" }), _jsx("button", { type: "button", className: "btn btn-primary", onClick: handleUpdateCourier, disabled: isUpdating || !selectedCourier, children: isUpdating ? (_jsxs("div", { children: [_jsx("span", { className: "spinner-border spinner-border-sm me-1", role: "status", "aria-hidden": "true" }), "\uCC98\uB9AC \uC911..."] })) : ('변경') })] })] }) }) }), _jsx("div", { className: "modal-backdrop fade show" })] })), showEditModal && editingOrder && (_jsxs("div", { children: [_jsx("div", { className: "modal fade show", style: { display: 'block' }, tabIndex: -1, children: _jsx("div", { className: "modal-dialog modal-xl", children: _jsxs("div", { className: "modal-content", children: [_jsxs("div", { className: "modal-header bg-light", children: [_jsx("h5", { className: "modal-title fw-bold", children: "\uC8FC\uBB38 \uC815\uBCF4 \uC218\uC815" }), _jsx("button", { type: "button", className: "btn-close", onClick: handleCloseEditModal })] }), _jsx("div", { className: "modal-body", children: _jsx("div", { className: "container-fluid", children: _jsx("div", { className: "row g-3", children: columns.map(col => {
                                                    var _a, _b, _c;
                                                    return (_jsxs("div", { className: "col-md-6", children: [_jsx("label", { className: "form-label", children: col.label }), col.key === 'orderStatus' ? (_jsxs("select", { className: "form-select", name: col.key, value: (_a = editingOrder[col.key]) !== null && _a !== void 0 ? _a : '', onChange: handleEditInputChange, children: [_jsx("option", { value: "", children: "\uC120\uD0DD" }), _jsx("option", { value: "\uB300\uAE30", children: "\uB300\uAE30" }), _jsx("option", { value: "\uCC98\uB9AC\uC911", children: "\uCC98\uB9AC\uC911" }), _jsx("option", { value: "\uC644\uB8CC", children: "\uC644\uB8CC" }), _jsx("option", { value: "\uCDE8\uC18C", children: "\uCDE8\uC18C" }), _jsx("option", { value: "\uC8FC\uBB38\uC811\uC218", children: "\uC8FC\uBB38\uC811\uC218" }), _jsx("option", { value: "\uACB0\uC81C\uC644\uB8CC", children: "\uACB0\uC81C\uC644\uB8CC" }), _jsx("option", { value: "\uC0C1\uD488\uC900\uBE44\uC911", children: "\uC0C1\uD488\uC900\uBE44\uC911" }), _jsx("option", { value: "\uBC30\uC1A1\uC900\uBE44\uC911", children: "\uBC30\uC1A1\uC900\uBE44\uC911" }), _jsx("option", { value: "\uBC30\uC1A1\uC911", children: "\uBC30\uC1A1\uC911" }), _jsx("option", { value: "\uBC30\uC1A1\uC644\uB8CC", children: "\uBC30\uC1A1\uC644\uB8CC" }), _jsx("option", { value: "\uC8FC\uBB38\uCDE8\uC18C", children: "\uC8FC\uBB38\uCDE8\uC18C" })] })) : col.isNumeric ? (_jsx("input", { type: "number", className: "form-control", name: col.key, value: (_b = editingOrder[col.key]) !== null && _b !== void 0 ? _b : '', onChange: handleEditInputChange })) : (_jsx("input", { type: "text", className: "form-control", name: col.key, value: (_c = editingOrder[col.key]) !== null && _c !== void 0 ? _c : '', onChange: handleEditInputChange }))] }, col.key));
                                                }) }) }) }), _jsxs("div", { className: "modal-footer bg-light", children: [_jsx("button", { type: "button", className: "btn btn-secondary", onClick: handleCloseEditModal, children: "\uCDE8\uC18C" }), _jsx("button", { type: "button", className: "btn btn-primary", onClick: () => handleSaveOrder(editingOrder), children: "\uC800\uC7A5" })] })] }) }) }), _jsx("div", { className: "modal-backdrop fade show" })] }))] }));
};
// cleaned legacy comment
const styles = `
  .cursor-pointer {
    cursor: pointer;
  }
  .cursor-pointer:hover {
    background-color: #f8f9fa;
  }
  .page-title-box {
    padding-top: 0.7rem !important;
    padding-bottom: 0.7rem !important;
    margin-bottom: 0.7rem !important;
  }
  .row.mb-1 {
    margin-bottom: 0.7rem !important;
  }
  .card .card-body.py-0, .card .card-body.py-2 {
    padding-top: 0.7rem !important;
    padding-bottom: 0.7rem !important;
  }
  .card {
    min-height: unset;
  }
  .card .d-flex.align-items-center {
    min-height: 36px;
    height: 70%;
  }
  .button-list .btn {
    padding-top: 0.28rem !important;
    padding-bottom: 0.28rem !important;
    font-size: 0.95rem !important;
  }
  .table-responsive::-webkit-scrollbar {
    height: 10px;
    width: 10px;
  }
  .table-responsive::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  .table-responsive::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 5px;
  }
  .table-responsive::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
  
  /* 탭 컨테이너 스타일 */
  .nav-tabs-custom {
    border-bottom: 2px solid #e3e6f0;
    display: flex;
    overflow-x: auto;
    flex-wrap: nowrap;
    gap: 5px;
  }
  
  /* 탭 기본 스타일 */
  .nav-tabs-custom .nav-item .nav-link {
    transition: all 0.3s ease;
    position: relative;
    border-radius: 6px 6px 0 0;
    padding: 0.6rem 1.2rem;
    margin-bottom: -2px;
    font-weight: 500;
    white-space: nowrap;
    border: none;
  }
  
  /* 비활성 탭 호버 효과 */
  .nav-tabs-custom .nav-item .nav-link:not(.active):hover {
    background-color: rgba(0, 0, 0, 0.04);
    transform: translateY(-2px);
  }
  
  /* 활성 탭 공통 스타일 */
  .nav-tabs-custom .nav-item .nav-link.active {
    font-weight: 700;
    border-width: 0;
    margin-top: -3px;
    padding-top: calc(0.6rem + 3px);
    position: relative;
    z-index: 2;
  }
  
  /* 활성 탭 아래 표시기 */
  .nav-tabs-custom .nav-item .nav-link.active::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 4px;
    border-radius: 4px 4px 0 0;
  }
  
  /* 탭 색상 - 전체 */
  .tab-all.active {
    background-color: rgba(78, 115, 223, 0.1) !important;
    color: #4e73df !important;
    box-shadow: 0 -4px 10px rgba(78, 115, 223, 0.1);
  }
  .tab-all.active::after {
    background-color: #4e73df !important;
  }
  
  /* 탭 색상 - 배송비 초과 */
  .tab-high-fee.active {
    background-color: rgba(231, 74, 59, 0.1) !important;
    color: #e74a3b !important;
    box-shadow: 0 -4px 10px rgba(231, 74, 59, 0.1);
  }
  .tab-high-fee.active::after {
    background-color: #e74a3b !important;
  }
  
  /* 탭 색상 - 공용 */
  .tab-common.active {
    background-color: rgba(28, 200, 138, 0.1) !important;
    color: #1cc88a !important;
    box-shadow: 0 -4px 10px rgba(28, 200, 138, 0.1);
  }
  .tab-common.active::after {
    background-color: #1cc88a !important;
  }
  
  /* 탭 색상 - 화물 */
  .tab-courier.active {
    background-color: rgba(54, 185, 204, 0.1) !important;
    color: #36b9cc !important;
    box-shadow: 0 -4px 10px rgba(54, 185, 204, 0.1);
  }
  .tab-courier.active::after {
    background-color: #36b9cc !important;
  }
  
  /* 생성된 탭의 배치 */
  .nav-link.active .badge {
    transition: all 0.3s ease;
    transform: scale(1.1);
    font-weight: bold;
  }
  
  /* 탭 배치 공통 스타일 */
  .nav-link .badge {
    transition: all 0.3s ease;
    padding: 0.35em 0.65em;
    font-size: 0.75em;
  }
  
  /* 탭 카운트 배지 스타일 */
  .tab-all.active .badge {
    color: #4e73df !important;
    background-color: #ffffff !important;
    box-shadow: 0 2px 5px rgba(78, 115, 223, 0.3);
  }
  
  .tab-high-fee.active .badge {
    color: #e74a3b !important;
    background-color: #ffffff !important;
    box-shadow: 0 2px 5px rgba(231, 74, 59, 0.3);
  }
  
  .tab-common.active .badge {
    color: #1cc88a !important;
    background-color: #ffffff !important;
    box-shadow: 0 2px 5px rgba(28, 200, 138, 0.3);
  }
  
  .tab-courier.active .badge {
    color: #36b9cc !important;
    background-color: #ffffff !important;
    box-shadow: 0 2px 5px rgba(54, 185, 204, 0.3);
  }

  .table-responsive::-webkit-scrollbar-track {
    background: #eef2f7;
  }
  .table-responsive::-webkit-scrollbar-thumb {
    background: #b7c1d1;
    border-radius: 999px;
  }
  .table-responsive::-webkit-scrollbar-thumb:hover {
    background: #95a3ba;
  }
  .allocation-top-layout {
    gap: 14px;
    align-items: stretch;
    margin-bottom: 12px;
  }
  .allocation-summary-strip .col-12,
  .allocation-actions-wrap .col-12 {
    height: 100%;
  }
  .allocation-summary-card,
  .allocation-actions-card,
  .allocation-tabs-card,
  .allocation-filter-card,
  .allocation-table-card {
    border: 1px solid #dfe6ef !important;
    border-radius: 16px !important;
    box-shadow: 0 8px 22px rgba(15, 23, 42, 0.06) !important;
    background: #ffffff !important;
  }
  .allocation-summary-card {
    min-height: 112px;
    overflow: hidden;
  }
  .allocation-summary-card .card-body {
    display: flex;
    align-items: center;
    padding: 18px 20px !important;
  }
  .allocation-summary-card .flex-grow-1 h4 {
    font-size: 1.55rem;
    font-weight: 800;
    color: #1f3b67;
  }
  .allocation-summary-card .flex-grow-1 p {
    font-size: 0.95rem;
    color: #8291a7 !important;
  }
  .allocation-summary-badge {
    width: 34px;
    height: 34px;
    border-radius: 8px !important;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: inset 0 -1px 0 rgba(255,255,255,0.25);
  }
  .allocation-summary-badge .avatar-title {
    font-size: 0.86rem;
    font-style: normal;
    font-weight: 700;
  }
  .allocation-actions-card .card-body,
  .allocation-tabs-card .card-body,
  .allocation-table-body {
    padding: 14px 16px !important;
  }
  .allocation-action-btn {
    min-height: 44px;
    padding: 0.7rem 1rem !important;
    border-radius: 10px !important;
    font-size: 0.9rem !important;
    font-weight: 700 !important;
    box-shadow: 0 6px 12px rgba(15, 23, 42, 0.08);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
  }
  .allocation-action-btn:disabled {
    box-shadow: none;
    opacity: 0.7;
  }
  .allocation-filter-card {
    padding: 16px !important;
    border-radius: 16px !important;
    border-color: #dfe6ef !important;
  }
  .allocation-filter-card input,
  .allocation-filter-card select,
  .allocation-filter-card button {
    border-radius: 10px !important;
  }
  .allocation-table-card {
    overflow: hidden;
  }
  .allocation-table-responsive {
    border: 1px solid #e7edf5;
    border-radius: 14px;
    background: #fbfcfe;
  }
  .allocation-table {
    margin-bottom: 0;
    border-color: #e4ebf3 !important;
    background: #ffffff;
  }
  .allocation-table thead th {
    background: #f8fafc !important;
    color: #60748f;
    font-weight: 700;
    border-color: #e4ebf3 !important;
    vertical-align: middle;
  }
  .allocation-table thead tr:nth-child(2) th {
    background: #ffffff !important;
    padding-top: 10px !important;
    padding-bottom: 10px !important;
  }
  .allocation-table thead input.form-control-sm {
    min-height: 34px;
    border-radius: 8px;
    border-color: #d7e0ec;
    font-size: 0.82rem;
  }
  .allocation-table tbody td {
    border-color: #edf2f7 !important;
    vertical-align: middle;
    color: #344054;
  }
  .allocation-table tbody tr:hover td {
    background-color: #f8fbff;
  }
  .allocation-total-fee {
    color: #243b63;
    font-size: 1rem !important;
    font-weight: 800;
    padding-left: 16px;
    white-space: nowrap;
  }
  .nav-tabs-custom {
    border-bottom: 1px solid #dde6f1;
    gap: 6px;
    padding-bottom: 2px;
  }
  .nav-tabs-custom .nav-item .nav-link {
    border-radius: 999px;
    padding: 0.7rem 1rem;
    font-weight: 700;
    border: 1px solid #e4ebf3;
    background: #ffffff;
    color: #5d6b82;
    margin-bottom: 0;
  }
  .nav-tabs-custom .nav-item .nav-link:not(.active):hover {
    background-color: #f8fbff;
    border-color: #cfe0f5;
    transform: translateY(-1px);
  }
  .nav-tabs-custom .nav-item .nav-link.active {
    border-color: #b9d5fb;
    background: linear-gradient(180deg, #f6fbff 0%, #ecf5ff 100%);
    color: #2884f6 !important;
    box-shadow: inset 0 -2px 0 #58a6ff;
  }
  .tab-all.active,
  .tab-high-fee.active,
  .tab-common.active,
  .tab-courier.active,
  .tab-confirm-request.active,
  .tab-cargo.active,
  .tab-jeju-island.active {
    color: #2884f6 !important;
    box-shadow: inset 0 -2px 0 #58a6ff;
  }
  .nav-link.active .badge {
    transform: scale(1.02);
    font-weight: 800;
  }
  .nav-link .badge {
    border-radius: 999px;
  }
  .tab-all.active .badge,
  .tab-high-fee.active .badge,
  .tab-common.active .badge,
  .tab-courier.active .badge,
  .tab-confirm-request.active .badge,
  .tab-cargo.active .badge,
  .tab-jeju-island.active .badge {
    color: #2884f6 !important;
    background-color: #ffffff !important;
    box-shadow: 0 2px 6px rgba(40, 132, 246, 0.18);
  }
  @media (max-width: 1400px) {
    .allocation-top-layout {
      flex-direction: column;
    }
    .allocation-summary-strip {
      width: 100% !important;
    }
  }
  @media (max-width: 768px) {
    .allocation-summary-card {
      min-height: 96px;
    }
    .allocation-summary-card .card-body {
      padding: 14px !important;
    }
    .allocation-action-btn {
      width: 100%;
    }
    .allocation-total-fee {
      padding-left: 0;
      padding-top: 10px;
      text-align: left !important;
    }
    .allocation-tabs-card .d-flex.justify-content-between {
      flex-direction: column;
      align-items: flex-start !important;
    }
  }
`;
export default AllocationManagement;
